import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client";
import {
  Device,
  HardwareInfo,
  SoftwareItem,
  StorageReport,
  Alert,
  DevicePatch,
  DevicePatchSummary,
  DeviceBackup,
  BitlockerActivity,
  BitlockerVolume,
  DeviceWarranty,
  AgentPin,
} from "../types";
import { useAuth } from "../context/AuthContext";
import AIAssistPanel from "../components/AIAssistPanel";
import DeviceFileBrowser from "../components/DeviceFileBrowser";
import { formatDate, formatDateTime, formatRelative, utcMillis } from "../utils/datetime";
import HardwareMonitor from "../components/HardwareMonitor";
import DeviceAlertingPanel from "../components/DeviceAlertingPanel";
import EventLogViewer from "../components/EventLogViewer";
import ScheduledTasksTab from "../components/ScheduledTasksTab";
import LocalUsersTab from "../components/LocalUsersTab";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";

/** True when the pin would take the device BACKWARDS from what it runs now.
 *
 *  A pin overrides the rollout in both directions, so this is not a corner
 *  case: a canary rollback is the main reason to pin below the fleet. The
 *  banner has to name the right event, because "it will not update" and "it
 *  will be downgraded" call for different decisions from whoever reads it.
 *
 *  Numeric per component, so 1.0.100 sorts above 1.0.99 — a plain string
 *  compare puts "1.0.100" BELOW "1.0.99" and would have started lying at the
 *  hundredth build. Any non-numeric or absent version returns false: the
 *  hold wording is the safe default, since it describes a pin that changes
 *  nothing about what is installed.
 */
function isPinDowngrade(pin: AgentPin): boolean {
  const parse = (v?: string | null) =>
    (v || "").split(".").map((p) => Number.parseInt(p, 10));
  const pinned = parse(pin.pinned_version);
  const running = parse(pin.running_version);
  if (!pinned.length || !running.length) return false;
  if (pinned.some(Number.isNaN) || running.some(Number.isNaN)) return false;
  for (let i = 0; i < Math.max(pinned.length, running.length); i++) {
    const a = pinned[i] ?? 0;
    const b = running[i] ?? 0;
    if (a !== b) return a < b;
  }
  return false;
}

export default function DeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canAck = user?.role === "admin" || user?.role === "technician";
  // WHAT "ADMIN" MEANS HERE, because this page had it wrong seven times over.
  //
  //   "The admin role just means the admin of AeroGauge. Technicians need to
  //    be able to administer the devices."
  //
  // Administering a DEVICE - reinstalling its agent, renaming it, encrypting
  // it, opening a shell on it, retiring it - is a technician's job. It is the
  // job. `admin` governs AeroGauge itself: licensing, users, roles, settings,
  // backup, audit. That line is what this constant encodes, and the split is
  // already clean everywhere else (every other adminOnly route in App.tsx is
  // platform administration).
  //
  // THE EVIDENCE THAT THIS WAS A UI-ONLY MISTAKE: of the seven actions gated
  // on `isAdmin` here, SIX already accepted technicians in the backend
  // (routers/devices.py - delete, reinstall, uninstall, rename, domain-join
  // /-leave, and every bitlocker route). Only the agent pin was genuinely
  // admin-only server-side, and that has been widened too. So the product
  // could do all of this for a technician the whole time; the page just never
  // drew the buttons. That is the two-gate failure again - granted in the
  // matrix, hidden in the UI, and the UI wins silently, so it gets reported
  // as a missing capability rather than as a permissions problem.
  const canAdminDevice = user?.role === "admin" || user?.role === "technician";
  // Remote console. The RBAC matrix already grants the `remote` feature to
  // technicians (services/rbac.py) and every backend gate on
  // /api/v1/screenshare accepts them - this button was the ONLY thing saying
  // otherwise, so a technician saw no way in and fell back to a third-party
  // remote tool. Connecting to a console is USING the product, not
  // administering it.
  //
  // WIDENING THE BUTTON WAS NOT ENOUGH, and this is worth remembering: the
  // popup navigates to /devices/:id/screen, which was still `adminOnly` in
  // App.tsx. A technician got the button, clicked it, and the new window
  // bounced to "/" - strictly worse than no button, because the product now
  // offered something it then refused. Screenshotting the button proved
  // nothing; only clicking it did.
  const canRemoteConsole = user?.role === "admin" || user?.role === "technician";
  const [device, setDevice] = useState<Device | null>(null);
  const [hardware, setHardware] = useState<HardwareInfo | null>(null);
  const [software, setSoftware] = useState<SoftwareItem[]>([]);
  const [storage, setStorage] = useState<StorageReport | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [patches, setPatches] = useState<DevicePatch[]>([]);
  const [patchSummary, setPatchSummary] = useState<DevicePatchSummary | null>(null);
  const [backups, setBackups] = useState<DeviceBackup[]>([]);
  const [bitlocker, setBitlocker] = useState<BitlockerVolume[]>([]);
  const [blActivity, setBlActivity] = useState<BitlockerActivity[]>([]);
  const [agentPin, setAgentPin] = useState<AgentPin | null>(null);
  const [warranty, setWarranty] = useState<DeviceWarranty | null>(null);
  const [warrantyRefreshing, setWarrantyRefreshing] = useState(false);

  const loadWarranty = useCallback(() => {
    api
      .get(`/api/v1/devices/${id}/warranty`)
      .then((r) => setWarranty(r.data))
      .catch(() => setWarranty(null));
  }, [id]);
  const refreshWarranty = async () => {
    setWarrantyRefreshing(true);
    try {
      const r = await api.post(`/api/v1/devices/${id}/warranty/refresh`);
      setWarranty(r.data);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Refresh failed");
    } finally {
      setWarrantyRefreshing(false);
    }
  };
  const [bitlockerReveal, setBitlockerReveal] = useState<{
    drive_letter: string;
    key: string;
  } | null>(null);
  const [revealing, setRevealing] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [swSearch, setSwSearch] = useState("");
  const bitlockerSort = useSort("drive_letter");
  const [blBusy, setBlBusy] = useState<number | null>(null);
  const [blMsg, setBlMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const softwareSort = useSort("name");
  const [tab, setTab] = useState<
    | "monitor"
    | "hardware"
    | "storage"
    | "software"
    | "services"
    | "tasks"
    | "localusers"
    | "files"
    | "events"
    | "patches"
    | "alerting"
  >("monitor");
  const [rebootOpen, setRebootOpen] = useState(false);
  const [rebootForm, setRebootForm] = useState({
    delay_seconds: 60,
    message: "AeroGauge RMM scheduled reboot — please save your work.",
    force: false,
    confirm_hostname: "",
    scheduled: false,
    schedule_at: "", // datetime-local, local wall-clock
  });
  const [rebootError, setRebootError] = useState("");
  const [rebootToast, setRebootToast] = useState("");
  const [offboardOpen, setOffboardOpen] = useState(false);
  const [offboardForm, setOffboardForm] = useState({
    mode: "uninstall" as "uninstall" | "factory_reset",
    confirm_hostname: "",
    remove_gpo: false,
    gpo_name: "",
  });
  const [offboardError, setOffboardError] = useState("");
  const [offboardBusy, setOffboardBusy] = useState(false);
  const [memberOpen, setMemberOpen] = useState(false);
  const [memberMode, setMemberMode] = useState<"rename" | "join" | "leave">("rename");
  const [memberForm, setMemberForm] = useState({
    new_name: "",
    domain: "",
    ou_path: "",
    workgroup: "WORKGROUP",
    username: "",
    password: "",
    reboot: true,
    confirm_hostname: "",
  });
  const [memberError, setMemberError] = useState("");
  const [memberBusy, setMemberBusy] = useState(false);

  const loadBlActivity = useCallback(() => {
    api
      .get(`/api/v1/devices/${id}/bitlocker/activity`)
      .then((r) => setBlActivity(r.data.actions || []))
      .catch(() => setBlActivity([]));
  }, [id]);

  const loadAlerts = useCallback(() => {
    api
      .get(`/api/v1/alerts`, { params: { device_id: id, include_resolved: false } })
      .then((r) => setAlerts(r.data))
      .catch(() => setAlerts([]));
  }, [id]);

  /** Why this machine is on an old agent, if it is on one deliberately.
   *
   *  A pin is the difference between "this device is behind, chase it" and
   *  "we are holding this device back on purpose, and here is who decided
   *  that and why". The API has stored that provenance since the pin
   *  feature shipped; nothing in the product ever showed it, so the only
   *  answer a tech could get was the version number and a shrug.
   *
   *  Swallows the error deliberately: this is read by any role that can
   *  open a device, and the endpoint is admin+technician, so a viewer just
   *  sees no badge rather than an error on an unrelated screen.
   */
  const loadAgentPin = useCallback(() => {
    api
      .get(`/api/v1/devices/${id}/agent-pin`)
      .then((r) => setAgentPin(r.data || null))
      .catch(() => setAgentPin(null));
  }, [id]);

  useEffect(() => {
    api.get(`/api/v1/devices/${id}`).then((r) => setDevice(r.data));
    api
      .get(`/api/v1/devices/${id}/hardware`)
      .then((r) => setHardware(r.data))
      .catch(() => {});
    api
      .get(`/api/v1/devices/${id}/software`)
      .then((r) => setSoftware(r.data))
      .catch(() => {});
    api
      .get(`/api/v1/devices/${id}/storage`)
      .then((r) => setStorage(r.data))
      .catch(() => {});
    api
      .get(`/api/v1/devices/${id}/patches`)
      .then((r) => setPatches(r.data))
      .catch(() => setPatches([]));
    api
      .get(`/api/v1/devices/${id}/patches/summary`)
      .then((r) => setPatchSummary(r.data))
      .catch(() => setPatchSummary(null));
    api
      .get(`/api/v1/devices/${id}/backups`)
      .then((r) => setBackups(r.data))
      .catch(() => setBackups([]));
    api
      .get(`/api/v1/devices/${id}/bitlocker`)
      .then((r) => setBitlocker(r.data))
      .catch(() => setBitlocker([]));
    loadBlActivity();
    loadWarranty();
    loadAlerts();
    loadAgentPin();
  }, [id, loadAlerts, loadWarranty, loadBlActivity, loadAgentPin]);

  /* REPORTED: "I clicked away from the screen and it did not save its status
     or progress. I had to click the button again." The banner was component
     state and the agent takes ten to twenty seconds to answer, so leaving the
     page in that window lost the outcome entirely. Poll while anything is
     still pending; stop as soon as nothing is. */
  useEffect(() => {
    if (!blActivity.some((a) => a.pending)) return;
    const t = setInterval(() => {
      loadBlActivity();
      api
        .get(`/api/v1/devices/${id}/bitlocker`)
        .then((r) => setBitlocker(r.data))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [blActivity, id, loadBlActivity]);

  const acknowledgeAlert = async (alertId: number) => {
    try {
      await api.post(`/api/v1/alerts/${alertId}/acknowledge`);
      loadAlerts();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to acknowledge alert");
    }
  };

  const revealBitlockerKey = async (volume: BitlockerVolume) => {
    if (!canAdminDevice) return;
    if (
      !window.confirm(
        `Reveal the Bitlocker recovery key for ${device?.hostname} ${volume.drive_letter}?\n\n` +
          `This action is recorded in the audit log with your username and timestamp.`,
      )
    )
      return;
    setRevealing(volume.id);
    try {
      const r = await api.get(`/api/v1/devices/${id}/bitlocker/${volume.id}/recovery-key`);
      setBitlockerReveal({ drive_letter: volume.drive_letter, key: r.data.recovery_password });
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to reveal recovery key");
    } finally {
      setRevealing(null);
    }
  };

  /** Enable / resume / disable BitLocker on one volume.
   *
   *  ASKED: "build the enable bitlocker / disable bitlocker button on the
   *  device under all devices... and displays the key. Would be awesome if
   *  the key / machine name could be save to credentials".
   *
   *  THREE actions rather than two, because the fleet has three states:
   *  never encrypted (enable, hours), encrypted-but-SUSPENDED (resume,
   *  seconds), and protected (disable). The suspended case is the one worth
   *  separating - such a volume keeps its key on the disk in the clear, so
   *  it reads as encrypted in an audit and protects nothing, and fixing it
   *  costs a moment.
   */
  const bitlockerAction = async (
    v: BitlockerVolume,
    action: "enable" | "resume" | "disable",
  ) => {
    const host = device?.hostname || "this device";
    const prompts: Record<string, string> = {
      enable:
        `Encrypt ${v.drive_letter} on ${host}?\n\n` +
        `A recovery key is created and escrowed here FIRST, then encryption ` +
        `runs in the background and can take hours. The machine stays usable ` +
        `throughout.\n\nNo boot PIN is added, so the machine will still start ` +
        `unattended.`,
      resume:
        `Resume BitLocker protection on ${v.drive_letter} (${host})?\n\n` +
        `This volume is already encrypted but protection is suspended, which ` +
        `means its key is on the disk in the clear. Resuming takes seconds.`,
      // The one that REMOVES protection gets the blunt wording, and the
      // server re-checks the confirmation - a confirm that lives only in the
      // browser is not a control.
      disable:
        `DECRYPT ${v.drive_letter} on ${host}?\n\n` +
        `This removes protection. The disk is readable by anyone who takes ` +
        `it from the moment this starts, not from when it finishes, and ` +
        `decryption takes as long as encryption did.\n\nOn a machine with a ` +
        `compliance obligation this is a reportable change.`,
    };
    if (!window.confirm(prompts[action])) return;
    setBlBusy(v.id);
    setBlMsg(null);
    try {
      const r = await api.post(`/api/v1/devices/${id}/bitlocker/${v.id}/action`, {
        action,
        confirm: action === "disable",
      });
      setBlMsg({ ok: true, text: r.data.message });
      // Pull the command row immediately so it shows as PENDING. That row,
      // not this banner, is what the tech sees if they leave and come back;
      // the polling effect takes over from here.
      loadBlActivity();
    } catch (err: any) {
      setBlMsg({
        ok: false,
        text: err.response?.data?.detail || `Could not ${action} BitLocker`,
      });
    } finally {
      setBlBusy(null);
    }
  };

  /** Act on a volume we have no row for - see the empty state on the card.
   *
   *  REPORTED: "I'm under all devices and pulled up a device I don't see the
   *  bitlocker stuff." 48 of 211 devices report no BitLocker volumes at all,
   *  so the card was not rendering and there was nothing to click - on
   *  exactly the machines most likely to need encrypting.
   */
  const bitlockerDeviceAction = async (
    action: "enable" | "resume" | "disable",
    drive: string,
  ) => {
    const host = device?.hostname || "this device";
    if (
      !window.confirm(
        `Encrypt ${drive}: on ${host}?\n\n` +
          `We have no BitLocker information for this machine, so this asks the ` +
          `agent to try. If the volume does not exist, or the machine has no ` +
          `TPM, the agent refuses and says why rather than half-configuring it.` +
          `\n\nA recovery key is created and escrowed here BEFORE anything is ` +
          `encrypted. No boot PIN is added.`,
      )
    )
      return;
    setBlBusy(-1);
    setBlMsg(null);
    try {
      const r = await api.post(`/api/v1/devices/${id}/bitlocker/action`, {
        action,
        drive_letter: drive,
      });
      setBlMsg({ ok: true, text: r.data.message });
      loadBlActivity();
    } catch (err: any) {
      setBlMsg({
        ok: false,
        text: err.response?.data?.detail || "Could not start",
      });
    } finally {
      setBlBusy(null);
    }
  };

  /** Ask the agent to re-run inventory, so "no data" can be told from "stale". */
  const rescanInventory = async () => {
    setBlBusy(-2);
    setBlMsg(null);
    try {
      const r = await api.post(`/api/v1/devices/${id}/inventory/refresh`);
      setBlMsg({ ok: true, text: r.data.message });
    } catch (err: any) {
      setBlMsg({
        ok: false,
        text: err.response?.data?.detail || "Could not queue the refresh",
      });
    } finally {
      setBlBusy(null);
    }
  };

  /** Copy the escrowed key into the customer's credential vault. */
  const bitlockerToVault = async (v: BitlockerVolume) => {
    setBlBusy(v.id);
    setBlMsg(null);
    try {
      const r = await api.post(
        `/api/v1/devices/${id}/bitlocker/${v.id}/save-to-vault`,
        {},
      );
      setBlMsg({
        ok: true,
        text: `${r.data.created ? "Saved" : "Updated"} in the credential vault as "${r.data.label}".`,
      });
    } catch (err: any) {
      setBlMsg({
        ok: false,
        text: err.response?.data?.detail || "Could not save to the vault",
      });
    } finally {
      setBlBusy(null);
    }
  };

  const metricLabel = (m: string) =>
    m === "disk_used_pct"
      ? "Disk Used"
      : m === "memory_used_pct"
        ? "Memory Used"
        : m === "cpu_used_pct"
          ? "CPU Used"
          : m === "battery_health_pct"
            ? "Battery Health"
            : m === "disk_health"
              ? "Disk Health"
              : m === "service_state"
                ? "Service"
                : m === "backup_health"
                  ? "Backup"
                  : m === "event_log"
                    ? "Event Log"
                    : m;

  const severityClasses = (s: string) =>
    s === "critical"
      ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800"
      : s === "warning"
        ? "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800"
        : "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";

  if (!device) return <div className="text-gray-400 p-8">Loading...</div>;

  const isWindows = !!device.os_version && /windows/i.test(device.os_version);
  const isServer = !!device.os_version && /server/i.test(device.os_version);

  const submitReboot = async () => {
    if (!device) return;
    setRebootError("");
    if (
      isServer &&
      rebootForm.confirm_hostname.trim().toLowerCase() !== device.hostname.toLowerCase()
    ) {
      setRebootError(`Type the hostname (${device.hostname}) exactly to confirm a server reboot.`);
      return;
    }
    if (rebootForm.scheduled && !rebootForm.schedule_at) {
      setRebootError("Pick a date & time for the scheduled reboot.");
      return;
    }
    try {
      if (rebootForm.scheduled) {
        // Device-scoped one_off reboot schedule. datetime-local is local
        // wall-clock; convert to UTC ISO (scheduler treats naive run_at as UTC).
        const runAtUtc = new Date(rebootForm.schedule_at).toISOString();
        await api.post(`/api/v1/reboot-schedules`, {
          name: `Reboot ${device.hostname}`,
          scope: "device",
          device_id: device.id,
          schedule_type: "one_off",
          run_at: runAtUtc,
          reboot_deferral_seconds: rebootForm.delay_seconds,
          reboot_message: rebootForm.message,
        });
        setRebootToast(
          `Reboot scheduled for ${device.hostname} at ${formatDateTime(rebootForm.schedule_at)}. It auto-enters maintenance mode around the reboot.`,
        );
      } else {
        await api.post(`/api/v1/devices/${device.id}/reboot`, {
          delay_seconds: rebootForm.delay_seconds,
          message: rebootForm.message,
          force: rebootForm.force,
        });
        setRebootToast(
          `Reboot queued for ${device.hostname}. Agent will run on next check-in (within 60 s) and trigger shutdown after the configured delay.`,
        );
      }
      setRebootOpen(false);
      setRebootForm({
        delay_seconds: 60,
        message: "AeroGauge RMM scheduled reboot — please save your work.",
        force: false,
        confirm_hostname: "",
        scheduled: false,
        schedule_at: "",
      });
      setTimeout(() => setRebootToast(""), 7000);
    } catch (err: any) {
      setRebootError(err.response?.data?.detail || "Failed to queue reboot");
    }
  };

  const reinstallAgent = async () => {
    if (!device) return;
    if (
      !window.confirm(
        `Reinstall the AeroGauge agent on ${device.hostname}? It downloads the latest agent ` +
          `and swaps it in (with automatic rollback if the new build won't start). The agent ` +
          `restarts during this, so the device briefly drops offline, then reports the new version.`,
      )
    )
      return;
    try {
      const r = await api.post(`/api/v1/devices/${device.id}/reinstall-agent`);
      setRebootToast(
        `Agent reinstall queued for ${device.hostname} (command #${r.data.command_id}). It runs on ` +
          `the next check-in (~60s); the device will briefly drop off as the agent restarts.`,
      );
      setTimeout(() => setRebootToast(""), 9000);
    } catch (err: any) {
      window.alert(err.response?.data?.detail || "Failed to queue agent reinstall");
    }
  };

  /** Renaming an AD-joined machine also renames its computer object in
   *  Active Directory, and the agent runs as SYSTEM — which authenticates
   *  as the machine account, and a machine account may not rename itself.
   *  So without a domain credential the domain refuses it, every time.
   *
   *  Only "ad" and "hybrid". Entra-joined renames are on record as working
   *  without credentials, and blocking on an unknown join type would stop
   *  renames that succeed today. The server enforces the same rule — this
   *  is here so the technician is told BEFORE they wait for a result.
   */
  const renameNeedsCreds =
    !!device &&
    memberMode === "rename" &&
    ["ad", "hybrid"].includes(device.identity_join_type || "");
  const memberBlocked = renameNeedsCreds && !memberForm.password.trim();

  const submitMembership = async () => {
    if (!device) return;
    setMemberError("");
    if (memberForm.confirm_hostname.trim().toLowerCase() !== device.hostname.toLowerCase()) {
      setMemberError(`Type the current hostname (${device.hostname}) to confirm.`);
      return;
    }
    setMemberBusy(true);
    try {
      let url = "";
      const body: any = { reboot: memberForm.reboot };
      if (memberMode === "rename") {
        url = `/api/v1/devices/${device.id}/rename`;
        body.new_name = memberForm.new_name.trim();
        body.username = memberForm.username.trim();
        body.password = memberForm.password;
      } else if (memberMode === "join") {
        url = `/api/v1/devices/${device.id}/domain-join`;
        body.domain = memberForm.domain.trim();
        body.ou_path = memberForm.ou_path.trim();
        body.new_name = memberForm.new_name.trim();
        body.username = memberForm.username.trim();
        body.password = memberForm.password;
      } else {
        url = `/api/v1/devices/${device.id}/domain-leave`;
        body.workgroup = memberForm.workgroup.trim();
        body.username = memberForm.username.trim();
        body.password = memberForm.password;
      }
      const r = await api.post(url, body);
      setMemberOpen(false);
      const label = memberMode === "rename" ? "Rename" : memberMode === "join" ? "Domain join" : "Domain leave";
      setRebootToast(
        `${label} queued for ${device.hostname} (command #${r.data.command_id}). Runs on the next ` +
          `check-in (~60s)` +
          (memberForm.reboot
            ? "; the device reboots ~60s after to finish."
            : " — a reboot is required to finish (you chose not to auto-reboot)."),
      );
      setTimeout(() => setRebootToast(""), 13000);
    } catch (err: any) {
      setMemberError(err.response?.data?.detail || "Failed to queue the change");
    } finally {
      setMemberBusy(false);
    }
  };

  const submitOffboard = async () => {
    if (!device) return;
    setOffboardError("");
    if (offboardForm.confirm_hostname.trim().toLowerCase() !== device.hostname.toLowerCase()) {
      setOffboardError(`Type the hostname (${device.hostname}) exactly to confirm.`);
      return;
    }
    setOffboardBusy(true);
    try {
      const r = await api.post(`/api/v1/devices/${device.id}/uninstall-agent`, {
        mode: offboardForm.mode,
        remove_gpo: offboardForm.mode === "factory_reset" && offboardForm.remove_gpo,
        gpo_name: offboardForm.gpo_name.trim(),
      });
      setOffboardOpen(false);
      setRebootToast(
        offboardForm.mode === "factory_reset"
          ? `Factory reset queued for ${device.hostname} (command #${r.data.command_id}). The agent ` +
            `uninstalls on its next check-in (~60s); the device record is removed once it stops reporting.`
          : `Agent uninstall queued for ${device.hostname} (command #${r.data.command_id}). It runs on ` +
            `the next check-in (~60s). The device record stays so you can reinstall later.`,
      );
      setTimeout(() => setRebootToast(""), 11000);
    } catch (err: any) {
      setOffboardError(err.response?.data?.detail || "Failed to queue uninstall");
    } finally {
      setOffboardBusy(false);
    }
  };

  const toggleMaintenance = async () => {
    if (!device) return;
    const entering = !device.maintenance_mode;
    try {
      await api.post(`/api/v1/devices/${device.id}/maintenance`, { enabled: entering });
      const r = await api.get(`/api/v1/devices/${device.id}`);
      setDevice(r.data);
      setRebootToast(
        entering
          ? `${device.hostname} is in maintenance mode — all alerting is muted until you exit.`
          : `${device.hostname} exited maintenance mode — alerting re-enabled.`,
      );
      setTimeout(() => setRebootToast(""), 6000);
    } catch (err: any) {
      window.alert(err.response?.data?.detail || "Failed to toggle maintenance mode");
    }
  };

  const toggleADRunner = async () => {
    if (!device) return;
    try {
      if (device.is_ad_runner) {
        if (!window.confirm("Stop using this device as the AD runner for this customer?")) return;
        await api.delete(`/api/v1/ad/runner/${device.id}`);
      } else {
        if (
          !window.confirm(
            "Designate this device as the AD runner for this customer? AD commands will execute here. Any other AD runner on this customer will be cleared.",
          )
        )
          return;
        await api.put(`/api/v1/ad/runner/${device.id}`);
      }
      const r = await api.get(`/api/v1/devices/${device.id}`);
      setDevice(r.data);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to update AD runner");
    }
  };

  const filteredSoftware = software.filter(
    (s) =>
      s.name.toLowerCase().includes(swSearch.toLowerCase()) ||
      (s.publisher || "").toLowerCase().includes(swSearch.toLowerCase()),
  );

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4 inline-block"
      >
        &larr; Back
      </button>

      {rebootToast && (
        <div className="mb-3 text-sm bg-orange-50 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-800 px-4 py-2 rounded-lg flex items-center gap-2">
          <span>⟳</span>
          {rebootToast}
        </div>
      )}

      <div className="mb-4">
        <AIAssistPanel deviceId={device.id} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{device.hostname}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
              {device.customer_name && (
                <span className="bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full text-xs">
                  {device.customer_name}
                </span>
              )}
              {device.group_name && <span className="text-gray-400">{device.group_name}</span>}
              <span>{device.ip_address}</span>
              {device.mac_address && (
                <span className="font-mono text-xs">{device.mac_address}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {device.is_ad_runner && (
              <span className="text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 px-3 py-1 rounded-full font-medium">
                AD Runner
              </span>
            )}
            {device.identity_join_type && device.identity_join_type !== "unknown" && (
              <span
                className={`text-xs px-3 py-1 rounded-full font-medium ${
                  device.identity_join_type === "hybrid"
                    ? "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                    : device.identity_join_type === "entra"
                      ? "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300"
                      : device.identity_join_type === "ad"
                        ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                }`}
                title={
                  device.entra_tenant_id
                    ? `Entra tenant: ${device.entra_tenant_id}`
                    : "Identity classification from dsregcmd /status"
                }
              >
                {device.identity_join_type === "hybrid"
                  ? "Hybrid"
                  : device.identity_join_type === "entra"
                    ? "Entra Joined"
                    : device.identity_join_type === "ad"
                      ? "AD Joined"
                      : device.identity_join_type === "workgroup"
                        ? "Workgroup"
                        : "Identity unknown"}
              </span>
            )}
            {device.pending_reboot && (
              <span className="text-xs bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-full font-medium">
                Reboot Pending
              </span>
            )}
            <span
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full font-medium ${device.status === "online" ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
            >
              <span
                className={`w-2 h-2 rounded-full ${device.status === "online" ? "bg-green-500" : "bg-red-500"}`}
              />
              {device.status}
            </span>
            {isWindows && (
              <button
                onClick={() => setRebootOpen(true)}
                className="text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-orange-700"
                title="Queue a reboot — agent will run shutdown /r on next check-in"
              >
                ⟳ Reboot
              </button>
            )}
            {canAdminDevice && (
              <button
                onClick={() => navigate(`/devices/${device.id}/shell`)}
                className="text-xs bg-slate-800 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-slate-900"
                title="Open an interactive PowerShell session on this device. Every keystroke is audit-logged."
              >
                ⌨ Open shell
              </button>
            )}
            {canRemoteConsole && isWindows && (
              <button
                onClick={async () => {
                  // Open the window SYNCHRONOUSLY (in the click gesture) so it's
                  // never popup-blocked, then create the session in THIS page
                  // (reliable) and hand the window the token. Errors surface here.
                  const w = window.open(
                    "",
                    `screenshare_${device.id}`,
                    "width=1280,height=800,resizable=yes,scrollbars=no",
                  );
                  try {
                    const r = await api.post("/api/v1/screenshare/sessions", {
                      device_id: device.id,
                      mode: "control",
                    });
                    const url = `/devices/${device.id}/screen?token=${encodeURIComponent(r.data.token)}`;
                    if (w) w.location.href = url;
                    else window.open(url, "_blank"); // popup blocked → tab
                  } catch (e: any) {
                    if (w) w.close();
                    window.alert(e.response?.data?.detail || "Could not start the screen share.");
                  }
                }}
                disabled={device.status !== "online"}
                className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50"
                title="Connect to this device's live console — view + control the desktop, including the lock/login screen, in a new window (no VPN). Requires the device online."
              >
                🖥 Connect to Console
              </button>
            )}
            {canAdminDevice && isWindows && (
              <ActionButton onAction={() => reinstallAgent()} variant="raw" className="text-xs bg-purple-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-purple-700" title="Re-push the latest agent to this device (downloads + swaps the exe, with rollback). Upgrades agents that can't self-update." pendingLabel="Installing…">
                ⤓ Reinstall agent
              </ActionButton>
            )}
            {canAdminDevice && isWindows && (
              <button
                onClick={() => {
                  setOffboardForm({
                    mode: "uninstall",
                    confirm_hostname: "",
                    remove_gpo: false,
                    gpo_name: "",
                  });
                  setOffboardError("");
                  setOffboardOpen(true);
                }}
                className="text-xs bg-rose-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-rose-700"
                title="Uninstall the agent from this device (offboarding). Optionally factory-reset to also delete the device record."
              >
                🗑 Uninstall agent
              </button>
            )}
            {canAdminDevice && isWindows && (
              <button
                onClick={() => {
                  const joined = device.identity_join_type === "ad" || device.identity_join_type === "hybrid";
                  setMemberMode(joined ? "leave" : "rename");
                  setMemberForm({
                    new_name: "",
                    domain: device.domain_name || "",
                    ou_path: "",
                    workgroup: "WORKGROUP",
                    username: "",
                    password: "",
                    reboot: true,
                    confirm_hostname: "",
                  });
                  setMemberError("");
                  setMemberOpen(true);
                }}
                className="text-xs bg-sky-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-sky-700"
                title="Rename this computer or change its domain / workgroup membership."
              >
                🏷 Rename / Domain
              </button>
            )}
            <button
              onClick={toggleMaintenance}
              role="switch"
              aria-checked={device.maintenance_mode}
              className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg font-medium border transition ${
                device.maintenance_mode
                  ? "bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300"
                  : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              }`}
              title={
                device.maintenance_mode
                  ? "Maintenance mode is ON — all alerting is muted. Click to turn off."
                  : "Maintenance mode is OFF. Click to turn on and mute ALL alerts (patching, reboots, etc.)."
              }
            >
              🔧 Maintenance
              <span
                className={`inline-flex h-4 w-8 items-center rounded-full px-0.5 transition ${
                  device.maintenance_mode ? "bg-amber-500 justify-end" : "bg-gray-300 dark:bg-gray-600 justify-start"
                }`}
              >
                <span className="h-3 w-3 rounded-full bg-white shadow" />
              </span>
              <span className="tabular-nums">{device.maintenance_mode ? "On" : "Off"}</span>
            </button>
            <RemoteDesktopButton deviceId={device.id} />
            <RemoteControlButton deviceId={device.id} />
            {canAdminDevice && device.customer_id && (
              <button
                onClick={toggleADRunner}
                className={`text-xs px-3 py-1 rounded-full border font-medium ${device.is_ad_runner ? "border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"}`}
              >
                {device.is_ad_runner ? "Clear AD Runner" : "Set as AD Runner"}
              </button>
            )}
            {canAdminDevice && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Permanently delete ${device.hostname}? This removes the device and all of ` +
                        `its collected data — inventory, patch history, alerts, and command ` +
                        `history. This action cannot be undone.`,
                    )
                  )
                    return;
                  try {
                    await api.delete(`/api/v1/devices/${device.id}`);
                    navigate("/devices");
                  } catch {
                    window.alert("The device could not be deleted. Please try again.");
                  }
                }}
                className="text-xs border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-1.5 rounded-lg font-medium hover:bg-red-50 dark:hover:bg-red-900/30"
                title="Permanently delete this device and its collected data"
              >
                🗑 Delete device
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">OS:</span>{" "}
            <span className="font-medium">{device.os_version || "—"}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Build:</span>{" "}
            <span className="font-medium">{device.os_build || "—"}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Agent:</span>{" "}
            <span className="font-medium">{device.agent_version || "—"}</span>
            {/* A pin is the answer to "why is this machine on an old agent?".
                The API has recorded who pinned it, when and why since the
                feature shipped, and none of it was ever on screen — so the
                only available answer was the version number and a shrug.
                Amber only when the pin holds the device BEHIND the fleet
                target; a pin at the current version is not a problem and
                should not be coloured like one. */}
            {agentPin?.pinned_version && (
              <span
                className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${
                  agentPin.behind_fleet_target
                    ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                }`}
                title={
                  `Pinned to ${agentPin.pinned_version}` +
                  (agentPin.pinned_by ? ` by ${agentPin.pinned_by}` : "") +
                  (agentPin.pinned_at ? ` on ${formatDate(agentPin.pinned_at)}` : "") +
                  (agentPin.reason ? `\n\nReason: ${agentPin.reason}` : "") +
                  (agentPin.behind_fleet_target && agentPin.fleet_target
                    ? `\n\nThe fleet is on ${agentPin.fleet_target}. This device is held behind it and is missing anything the newer agent fixed.`
                    : "")
                }
              >
                📌 Pinned {agentPin.pinned_version}
              </span>
            )}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Domain:</span>{" "}
            <span className="font-medium">
              {/* "Workgroup" is an ASSERTION, so only make it when the agent
                  actually reported one. A domain controller whose identity
                  probe failed was being labelled Workgroup - see
                  agent/identity.py, where a non-zero dsregcmd exit code threw
                  away a perfectly good status block. */}
              {device.identity_join_type === "ad" || device.identity_join_type === "hybrid"
                ? device.domain_name || "Domain-joined"
                : device.identity_join_type === "entra"
                  ? "Entra ID"
                  : device.identity_join_type === "workgroup"
                    ? device.domain_name || "Workgroup"
                    : "Not reported yet"}
            </span>
          </div>
          {device.gotoassist_installed && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">GoToAssist:</span>{" "}
              <span className="font-medium text-green-600">Installed</span>
            </div>
          )}
          <div>
            <span className="text-gray-500 dark:text-gray-400">EDR:</span>{" "}
            {(() => {
              // Prefer the agent-detected security products (generic — any
              // vendor). Fall back to the Huntress cloud-sync edr_status, then
              // the legacy huntress_installed boolean, so nothing regresses on
              // devices whose agent hasn't reported security products yet.
              const edrProducts = (hardware?.security_products || []).filter(
                (p) => p.type === "edr",
              );
              if (edrProducts.length > 0) {
                return (
                  <span
                    className="font-medium text-green-600"
                    title={edrProducts.map((p) => p.name).join(", ")}
                  >
                    {edrProducts.map((p) => p.name).join(", ")}
                  </span>
                );
              }
              const s = device.edr_status;
              if (s === "secure") {
                return <span className="font-medium text-green-600">Secure</span>;
              }
              if (s === "at_risk") {
                return <span className="font-medium text-amber-600">At risk</span>;
              }
              if (s === "breached") {
                return <span className="font-medium text-red-600">Breach detected</span>;
              }
              if (s === "unmanaged") {
                return <span className="font-medium text-orange-500">Unmanaged</span>;
              }
              return (
                <span
                  className={`font-medium ${device.huntress_installed ? "text-green-600" : "text-orange-500"}`}
                >
                  {device.huntress_installed ? "Installed" : "Pending"}
                </span>
              );
            })()}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Backup:</span>{" "}
            {(() => {
              const s = device.backup_status;
              const detected = (hardware?.backup_products || [])
                .map((p) => p.name)
                .filter(Boolean);
              // No backup-integration status → fall back to the agent-detected
              // backup software (vendor-agnostic), else nothing found.
              if (!s) {
                return detected.length ? (
                  <span className="font-medium text-gray-700 dark:text-gray-200">
                    {detected.join(", ")}
                  </span>
                ) : (
                  <span className="font-medium text-gray-400">Not detected</span>
                );
              }
              if (s === "success") return <span className="font-medium text-green-600">OK</span>;
              if (s === "running")
                return <span className="font-medium text-blue-600">Running…</span>;
              if (s === "partial")
                return <span className="font-medium text-amber-600">Partial</span>;
              if (s === "failed") return <span className="font-medium text-red-600">Failed</span>;
              if (s === "no_recent")
                return <span className="font-medium text-amber-600">Stale (&gt;7d)</span>;
              if (s === "never") return <span className="font-medium text-orange-500">Never</span>;
              if (s === "unmanaged")
                return <span className="font-medium text-gray-400">Unmanaged</span>;
              return <span className="font-medium text-gray-500">{s}</span>;
            })()}
            {device.backup_last_run_at && (
              <span className="text-gray-400 text-xs ml-1">
                ({formatRelative(device.backup_last_run_at)})
              </span>
            )}
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Last Seen:</span>{" "}
            <span className="font-medium">
              {device.last_seen
                ? formatRelative(device.last_seen)
                : "Never"}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Added:</span>{" "}
            <span className="font-medium">{formatDate(device.created_at)}</span>
          </div>
          {device.last_disk_health &&
            device.last_disk_health.length > 0 &&
            (() => {
              const bad = device.last_disk_health.filter((d) => d.health === "Unhealthy");
              const warn = device.last_disk_health.filter((d) => d.health === "Warning");
              const cls = bad.length
                ? "text-red-600"
                : warn.length
                  ? "text-orange-500"
                  : "text-green-600";
              const text = bad.length
                ? `Unhealthy (${bad.length})`
                : warn.length
                  ? `Warning (${warn.length})`
                  : "Healthy";
              // Plain-English explainer of what the SMART status means, plus
              // the per-drive breakdown, in the hover tooltip.
              const explainer = bad.length
                ? "SMART is the drive's self-diagnostics. 'Unhealthy' means a drive is reporting failure indicators (reallocated sectors, read errors) — back up and plan a replacement now."
                : warn.length
                  ? "SMART is the drive's self-diagnostics. 'Warning' means a drive is showing early wear/errors — keep an eye on it and make sure backups are current."
                  : "SMART is the drive's built-in self-diagnostics. 'Healthy' means every drive passes its self-checks.";
              const drives = device.last_disk_health
                .map((d) => `${d.model} [${d.health}]`)
                .join("\n");
              return (
                <div title={`${explainer}\n\n${drives}`}>
                  <span className="text-gray-500 dark:text-gray-400">
                    Disk Health <span className="text-gray-300 dark:text-gray-600">(SMART)</span>:
                  </span>{" "}
                  <span className={`font-medium ${cls}`}>{text}</span>
                  <span
                    className="ml-1 text-gray-300 dark:text-gray-600 cursor-help"
                    title={explainer}
                  >
                    ⓘ
                  </span>
                </div>
              );
            })()}
          {device.has_battery &&
            typeof device.last_battery_health_pct === "number" &&
            (() => {
              const p = device.last_battery_health_pct;
              const cls =
                p < 30
                  ? "text-red-600"
                  : p < 50
                    ? "text-orange-500"
                    : p < 70
                      ? "text-yellow-600"
                      : "text-green-600";
              return (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Battery Health:</span>{" "}
                  <span className={`font-medium ${cls}`}>{p.toFixed(0)}%</span>
                </div>
              );
            })()}
          {backups.map((b) => {
            const vendor =
              b.vendor === "windows_server_backup"
                ? "WSB"
                : b.vendor === "veeam_agent"
                  ? "Veeam Agent"
                  : b.vendor;
            const ts =
              b.last_status === "success"
                ? b.last_success_at
                : b.last_failure_at || b.last_success_at;
            const ago = ts ? formatRelative(ts) : "never";
            const cls =
              b.last_status === "success"
                ? "text-green-600"
                : b.last_status === "failed"
                  ? "text-red-600"
                  : b.last_status === "in_progress"
                    ? "text-blue-600"
                    : "text-gray-500";
            const icon =
              b.last_status === "success"
                ? "✓"
                : b.last_status === "failed"
                  ? "✕"
                  : b.last_status === "in_progress"
                    ? "…"
                    : "?";
            return (
              <div key={b.id} title={b.last_error_message || `${b.vendor} · ${b.last_status}`}>
                <span className="text-gray-500 dark:text-gray-400">{vendor}:</span>{" "}
                <span className={`font-medium ${cls}`}>
                  {icon} {ago}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {device.stale_duplicate && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 mb-6 text-sm text-amber-900 dark:text-amber-200 flex items-start justify-between gap-4">
          <div>
            ⚠ This looks like a <strong>stale duplicate</strong> — there's a newer/online record with
            the same hostname (likely a re-enrollment). Actions here (AI assistant, services, scans)
            will time out. Use the current record, and delete this one when convenient.
          </div>
          {canAdminDevice && (
            <button
              onClick={async () => {
                if (
                  !window.confirm(
                    `Delete this stale duplicate of ${device.hostname}? The current (newer/online) ` +
                      `record is kept — only this outdated copy and its data are removed. Cannot be undone.`,
                  )
                )
                  return;
                try {
                  await api.delete(`/api/v1/devices/${device.id}`);
                  navigate("/devices");
                } catch {
                  window.alert("The duplicate could not be deleted. Please try again.");
                }
              }}
              className="shrink-0 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-amber-700"
              title="Delete this stale duplicate record (keeps the current record)"
            >
              🗑 Delete duplicate
            </button>
          )}
        </div>
      )}

      {/* A PIN HELD BEHIND THE FLEET GETS A BANNER, NOT JUST A BADGE.
          Three stale canary pins were once found still freezing devices, two
          of them AD runners held back from a fix a customer had reported, and
          nobody could tell a deliberate hold from a leftover. The router
          comment on /agent-pins says "a pin nobody can see is a pin nobody
          removes" — but the endpoint had no caller, so the pin was still
          invisible to every human using the product. A tooltip would not have
          fixed that either: you have to know to hover.

          Only when it is BEHIND the target. A pin at the current version
          costs nothing and does not deserve a banner. */}
      {agentPin?.behind_fleet_target && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl p-4 mb-6 text-sm text-amber-900 dark:text-amber-200 flex items-start justify-between gap-4">
          <div>
            📌 This device is <strong>pinned to agent {agentPin.pinned_version}</strong> while the
            fleet is on {agentPin.fleet_target || "a newer version"}, so{" "}
            {/* A pin overrides the rollout in BOTH directions - should_update()
                pushes the pinned build whenever it differs from the running
                one, so a pin BELOW the running version actively DOWNGRADES
                the machine. Observed doing exactly that: pinning a device
                already on 1.0.99 back to 1.0.97 sent it 1.0.97 on the next
                check-in. Saying "it will not update" in that case would
                describe the wrong event entirely. */}
            {isPinDowngrade(agentPin) ? (
              <>
                it will be <strong>downgraded to {agentPin.pinned_version}</strong> from the{" "}
                {agentPin.running_version} it is running now, losing anything fixed in between.
              </>
            ) : (
              <>it will not update and is missing anything the newer agent fixed.</>
            )}
            {agentPin.is_ad_runner && (
              <>
                {" "}
                <strong>It is this customer's directory runner</strong>, so the hold affects their
                Active Directory features too.
              </>
            )}
            <div className="mt-1.5 text-xs">
              {agentPin.reason ? <>Reason given: “{agentPin.reason}”. </> : null}
              {agentPin.pinned_by ? <>Pinned by {agentPin.pinned_by}</> : null}
              {agentPin.pinned_at ? <> on {formatDate(agentPin.pinned_at)}</> : null}
              {typeof agentPin.age_days === "number" ? <> ({agentPin.age_days}d ago)</> : null}.
            </div>
          </div>
          {canAdminDevice && (
            <ActionButton
              variant="raw"
              className="shrink-0 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-amber-700 disabled:opacity-60"
              pendingLabel="Releasing…"
              title={`Remove the pin so ${device.hostname} follows the fleet rollout again`}
              onAction={async () => {
                if (
                  !window.confirm(
                    `Release the agent pin on ${device.hostname}?\n\n` +
                      `It will update to ${agentPin.fleet_target || "the fleet version"} on its ` +
                      `next check-in. The reason recorded for the pin was:\n\n` +
                      `${agentPin.reason || "(none)"}`,
                  )
                )
                  return;
                try {
                  await api.delete(`/api/v1/devices/${device.id}/agent-pin`);
                  loadAgentPin();
                } catch (e: any) {
                  window.alert(e.response?.data?.detail || "The pin could not be released.");
                }
              }}
            >
              Release pin
            </ActionButton>
          )}
        </div>
      )}

      <AIDeviceDescription deviceId={device.id} />

      {alerts.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Active Alerts <span className="text-gray-400 font-normal">({alerts.length})</span>
            </h2>
          </div>
          <div className="space-y-2">
            {alerts.map((a) => (
              <div
                key={a.id}
                className={`border rounded-lg px-4 py-3 flex items-start gap-3 ${severityClasses(a.severity)}`}
              >
                <span className="text-xs font-bold uppercase tracking-wide whitespace-nowrap pt-0.5">
                  {a.severity}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {metricLabel(a.metric)}
                    {a.target ? ` — ${a.target}:` : ""}
                    {typeof a.value === "number" ? ` at ${a.value.toFixed(0)}%` : ""}
                  </div>
                  {a.message && <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{a.message}</div>}
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-3">
                    <span>
                      opened {formatRelative(a.created_at)}
                    </span>
                    {a.acknowledged_at && (
                      <span className="bg-white/60 dark:bg-white/10 px-2 py-0.5 rounded-full text-gray-600 dark:text-gray-300">
                        ack'd{" "}
                        {formatRelative(a.acknowledged_at)}
                      </span>
                    )}
                  </div>
                </div>
                {canAck && !a.acknowledged_at && (
                  <button
                    onClick={() => acknowledgeAlert(a.id)}
                    className="text-xs bg-white/70 dark:bg-white/10 hover:bg-white dark:hover:bg-white/20 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-md border border-gray-200 dark:border-gray-600"
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ALWAYS rendered on a Windows device, even with no volumes.
          REPORTED: "I'm under all devices and pulled up a device I don't see
          the bitlocker stuff." The card was gated on `bitlocker.length > 0`
          and 48 of 211 devices have no volume rows - the agent's
          Get-BitLockerVolume returned nothing, or inventory has not run since
          it started collecting. Those are precisely the machines most likely
          to need encrypting, and they had no card and so no button. */}
      {(bitlocker.length > 0 ||
        (device?.os_version || "").toLowerCase().includes("windows")) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              BitLocker{" "}
              <span className="text-gray-400 font-normal">
                {bitlocker.length > 0
                  ? `(${bitlocker.length} volume${bitlocker.length === 1 ? "" : "s"})`
                  : "(no volumes reported)"}
              </span>
            </h2>
            {!canAdminDevice && (
              <span className="text-xs text-gray-400">
                Recovery keys need device-administration rights.
              </span>
            )}
          </div>

          {/* The outcome of an action SURVIVES LEAVING THE PAGE.
              REPORTED: "I clicked away from the screen and it did not save
              its status or progress. I had to click the button again." The
              banner below was component state, and the agent takes ten to
              twenty seconds to answer, so navigating away in that window lost
              the answer. This reads the command rows instead. */}
          {/* FEEDBACK, NOT HISTORY. Reported with a screenshot: a resume that
              failed five days earlier was still sitting here in red, saying
              "The term 'Get-BitLockerVolume' is not recognized... Check the
              spelling of the name" - directly above the panel that calmly
              explains this machine has no BitLocker volumes and why. The page
              contradicted itself, and the louder half was both stale and
              addressed to whoever wrote the agent rather than to the person
              reading it. "We shouldn't be showing the part of command
              failure."
              `stale` is computed server-side so the rule is one thing in one
              place; pending is never stale. */}
          {blActivity.some((a) => !a.stale) && (
            <div className="mb-3 space-y-1.5">
              {blActivity
                .filter((a) => !a.stale)
                .slice(0, 3)
                .map((a) => {
                const verb =
                  a.action === "enable"
                    ? "Enable BitLocker"
                    : a.action === "resume"
                      ? "Resume protection"
                      : "Disable BitLocker";
                const ok = a.status === "completed";
                return (
                  <div
                    key={a.command_id}
                    className={`text-xs rounded-lg border px-3 py-2 ${
                      a.pending
                        ? "border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"
                        : ok
                          ? "border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300"
                          : "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300"
                    }`}
                  >
                    <span className="font-medium">
                      {verb}
                      {a.drive_letter ? ` on ${a.drive_letter}` : ""}
                    </span>{" "}
                    —{" "}
                    {a.pending
                      ? "waiting for the device to pick it up (it checks in every minute or so)"
                      : ok
                        ? a.changed === false
                          ? a.reason || "nothing to change"
                          : "done"
                        : a.reason || "failed"}
                    <span className="block text-[11px] opacity-70 mt-0.5">
                      {formatDateTime(a.completed_at || a.requested_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {bitlocker.length === 0 && (
            <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-sm">
              <p className="text-gray-600 dark:text-gray-300">
                This device has not reported any BitLocker volumes. That means
                one of three things: BitLocker is not installed or this Windows
                edition does not include it, the system drive has never been
                encrypted, or inventory has not run since the agent started
                collecting encryption data.
              </p>
              {device?.tpm_state === "absent" && (
                <p className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-3 py-2 mt-3 text-xs">
                  This machine reports <strong>no TPM</strong>, so the operating-system
                  volume cannot be encrypted without a boot PIN or a USB startup key —
                  neither of which is set up remotely. Add a TPM (on a VM, enable the
                  virtual TPM) first.
                </p>
              )}
              {canAdminDevice && device?.tpm_state !== "absent" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => bitlockerDeviceAction("enable", "C")}
                    disabled={blBusy === -1}
                    title="Creates and escrows a recovery key first, then encrypts C: in the background. Refused with a reason if there is no usable TPM."
                    className="text-xs bg-green-700 text-white px-3 py-1.5 rounded hover:bg-green-800 disabled:opacity-50"
                  >
                    {blBusy === -1 ? "Working…" : "Enable BitLocker on C:"}
                  </button>
                  <button
                    onClick={rescanInventory}
                    disabled={blBusy === -2}
                    className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {blBusy === -2 ? "Queuing…" : "Rescan inventory"}
                  </button>
                </div>
              )}
            </div>
          )}
          {bitlocker.length > 0 && (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <SortableTh label="Drive" sortKey="drive_letter" sort={bitlockerSort.sort} onSort={bitlockerSort.toggle} />
                <SortableTh label="Protection" sortKey="protection_status" sort={bitlockerSort.sort} onSort={bitlockerSort.toggle} />
                <SortableTh label="Encryption" sortKey="encryption_method" sort={bitlockerSort.sort} onSort={bitlockerSort.toggle} />
                <SortableTh label="Recovery key" sortKey="has_recovery_key" sort={bitlockerSort.sort} onSort={bitlockerSort.toggle} />
                {canAdminDevice && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(bitlocker, bitlockerSort.sort, (v, key) => {
                switch (key) {
                  case "protection_status":
                    return (v.protection_status || "").toLowerCase();
                  case "encryption_method":
                    return (v.encryption_method || "").toLowerCase();
                  case "has_recovery_key":
                    return v.has_recovery_key ? 1 : 0;
                  default:
                    return (v.drive_letter || "").toLowerCase();
                }
              }).map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5 font-mono text-gray-800 dark:text-gray-200">{v.drive_letter}</td>
                  <td className="px-4 py-2.5">
                    {v.protection_status === "On" ? (
                      <span className="text-xs bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-2 py-0.5 rounded-full">
                        On
                      </span>
                    ) : v.protection_status === "Off" ? (
                      <span className="text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 px-2 py-0.5 rounded-full">
                        Off
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                        {v.protection_status}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-400">
                    {v.encryption_method || "—"}
                    {typeof v.encryption_percentage === "number" &&
                      v.encryption_percentage < 100 && (
                        <span className="ml-2 text-orange-600">{v.encryption_percentage}%</span>
                      )}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {v.has_recovery_key ? (
                      <span className="text-green-700">✓ Escrowed</span>
                    ) : (
                      <span className="text-red-600">✕ Not escrowed</span>
                    )}
                  </td>
                  {canAdminDevice && (
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1.5 justify-end">
                        {v.has_recovery_key && (
                          <>
                            <button
                              onClick={() => revealBitlockerKey(v)}
                              disabled={revealing === v.id}
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {revealing === v.id ? "Revealing…" : "Show recovery key"}
                            </button>
                            <button
                              onClick={() => bitlockerToVault(v)}
                              disabled={blBusy === v.id}
                              title="Copy this key into the customer's credential vault, labelled with the machine name"
                              className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                            >
                              {blBusy === v.id ? "Saving…" : "Save to vault"}
                            </button>
                          </>
                        )}
                        {/* ASKED: "I was also not seeing the ability to vault
                            the key... Is that going to show after it is
                            encrypted?" No - it shows once the KEY reaches us,
                            which happens BEFORE encryption starts, not after
                            it finishes. Saying so beats an absent button. */}
                        {!v.has_recovery_key && (
                          <span
                            className="text-xs text-gray-400 dark:text-gray-500 self-center"
                            title="The recovery key is created and escrowed before any data is encrypted. It appears here on the device's next check-in — you do not have to wait for encryption to finish."
                          >
                            No key escrowed yet
                          </span>
                        )}
                        {/* Which action is offered depends on the state the
                            volume is actually in. Showing "Enable" next to an
                            encrypted volume, or "Resume" next to one that was
                            never encrypted, is how a tech clicks the wrong
                            thing. */}
                        {v.protection_status === "On" ? (
                          <button
                            onClick={() => bitlockerAction(v, "disable")}
                            disabled={blBusy === v.id}
                            className="text-xs border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                          >
                            {blBusy === v.id ? "Working…" : "Disable"}
                          </button>
                        ) : v.encryption_method && v.encryption_method !== "None" ? (
                          <button
                            onClick={() => bitlockerAction(v, "resume")}
                            disabled={blBusy === v.id}
                            title="Encrypted but protection is suspended — the key is on the disk in the clear. This takes seconds."
                            className="text-xs bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                          >
                            {blBusy === v.id ? "Working…" : "Resume protection"}
                          </button>
                        ) : device?.tpm_state === "absent" ? (
                          /* Do not offer what cannot work. REPORTED: two
                             clicks on Enable, both refused by the agent
                             nineteen seconds later for want of a TPM, and
                             nothing on screen said so. */
                          <span
                            className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2.5 py-1 rounded"
                            title="Encrypting the OS volume without a TPM needs a boot PIN or USB startup key. Neither is set up remotely — on a machine you cannot reach that is an outage that looks like a successful encryption until the next reboot."
                          >
                            No TPM — cannot encrypt
                          </span>
                        ) : (
                          <button
                            onClick={() => bitlockerAction(v, "enable")}
                            disabled={blBusy === v.id}
                            title={
                              device?.tpm_state === "notready"
                                ? "A TPM is present but not initialised — this will probably be refused until it is owned/initialised on the machine"
                                : "Creates and escrows a recovery key first, then encrypts in the background"
                            }
                            className="text-xs bg-green-700 text-white px-3 py-1 rounded hover:bg-green-800 disabled:opacity-50"
                          >
                            {blBusy === v.id ? "Working…" : "Enable BitLocker"}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          )}
          {blMsg && (
            <div
              className={`px-4 py-2.5 text-xs border-t ${
                blMsg.ok
                  ? "border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300"
                  : "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300"
              }`}
            >
              {blMsg.text}
            </div>
          )}
        </div>
      )}

      {bitlockerReveal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
              BitLocker recovery key — {bitlockerReveal.drive_letter}
            </h2>
            <p className="text-xs text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded px-3 py-2 mb-4">
              ⚠ This reveal has been recorded in the audit log under your account. Copy the key only
              if you have a legitimate operational need.
            </p>
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded p-3 mb-4 font-mono text-lg select-all break-all text-gray-900 dark:text-gray-100">
              {bitlockerReveal.key}
            </div>
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(bitlockerReveal.key);
                }}
                className="text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-1.5 rounded-lg"
              >
                Copy to clipboard
              </button>
              <button
                onClick={() => setBitlockerReveal(null)}
                className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-4 border-b dark:border-gray-700 overflow-x-auto">
        {(
          [
            "monitor",
            "hardware",
            "storage",
            "software",
            "services",
            "tasks",
            "localusers",
            "files",
            "events",
            "patches",
            "alerting",
          ] as const
        ).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400" : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
          >
            {t === "monitor"
              ? "Summary"
              : t === "software"
                ? `Software (${software.length})`
                : t === "services"
                  ? "Services"
                  : t === "tasks"
                  ? "Scheduled Tasks"
                  : t === "localusers"
                  ? "Local Users"
                  : t === "files"
                    ? "Files"
                    : t === "events"
                    ? "Event Viewer"
                    : t === "storage"
                    ? `Storage & Shares`
                    : t === "patches"
                      ? `Patches${patchSummary ? ` (${patchSummary.pending})` : ""}`
                      : t === "alerting"
                        ? "Alerting & Maintenance"
                        : "Hardware"}
          </button>
        ))}
      </div>

      {tab === "monitor" && device && (
        <HardwareMonitor
          device={device}
          storage={storage}
          canManage={canAdminDevice}
          // REPORTED: "I am not seeing on the summary screen a total ram /
          // total CPU (cores and threads)." The gauges showed percentages
          // with nothing to be a percentage OF - 62% of how much? - and the
          // totals lived one tab away under Hardware.
          hardware={hardware}
        />
      )}

      {tab === "services" && device && (
        <ServicesTab deviceId={device.id} online={device.status === "online"} canManage={canAdminDevice} />
      )}

      {tab === "files" && device && <FilesTab deviceId={device.id} />}

      {tab === "events" && device && <EventLogViewer deviceId={device.id} online={device.status === "online"} />}

      {tab === "tasks" && device && (
        <ScheduledTasksTab deviceId={device.id} online={device.status === "online"} canManage={canAdminDevice} />
      )}

      {tab === "localusers" && device && (
        <LocalUsersTab deviceId={device.id} online={device.status === "online"} canManage={canAdminDevice} />
      )}

      {tab === "alerting" && device && <DeviceAlertingPanel deviceId={device.id} />}

      {tab === "hardware" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
            {hardware ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  ["Manufacturer", hardware.manufacturer],
                  ["Model", hardware.model],
                  ["Serial Number", hardware.serial_number],
                  ["CPU", hardware.cpu_model],
                  // Cores AND threads. Cores alone under-describes anything
                  // with hyper-threading, which is most of the fleet.
                  [
                    "CPU Cores",
                    hardware.cpu_cores
                      ? hardware.cpu_threads
                        ? `${hardware.cpu_cores} cores / ${hardware.cpu_threads} threads`
                        : `${hardware.cpu_cores} cores`
                      : null,
                  ],
                  // Only when there is more than one - "1 socket" is noise,
                  // and "2 sockets" changes how the box is licensed.
                  [
                    "CPU Sockets",
                    hardware.cpu_sockets && hardware.cpu_sockets > 1 ? hardware.cpu_sockets : null,
                  ],
                  ["RAM", hardware.ram_gb ? `${hardware.ram_gb} GB` : null],
                  ["Disk Total", hardware.disk_total_gb ? `${hardware.disk_total_gb} GB` : null],
                  ["Disk Free", hardware.disk_free_gb ? `${hardware.disk_free_gb} GB` : null],
                  ["BIOS Version", hardware.bios_version],
                ].map(([label, value]) => (
                  <div key={label as string}>
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{value || "—"}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">
                Hardware info will appear after the next agent check-in.
              </p>
            )}
          </div>

          {hardware?.nics && hardware.nics.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                Network Adapters ({hardware.nics.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs text-gray-500 dark:text-gray-400 uppercase border-b dark:border-gray-700">
                    <tr>
                      <th className="text-left py-2 pr-4">Adapter</th>
                      <th className="text-left py-2 pr-4">Status</th>
                      <th className="text-left py-2 pr-4">IP addresses</th>
                      <th className="text-left py-2 pr-4">MAC</th>
                      <th className="text-left py-2 pr-4">Speed</th>
                      <th className="text-left py-2">DHCP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {hardware.nics.map((n, i) => {
                      const up = (n.status || "").toLowerCase() === "up";
                      const ips = [...(n.ipv4 || []), ...(n.ipv6 || [])];
                      return (
                        <tr key={i} className="align-top">
                          <td className="py-2 pr-4">
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {n.name || "—"}
                            </div>
                            <div className="text-xs text-gray-400">{n.description}</div>
                          </td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${up ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                            >
                              {n.status || "—"}
                            </span>
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs">
                            {ips.length ? (
                              ips.map((ip, j) => <div key={j}>{ip}</div>)
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs">{n.mac || "—"}</td>
                          <td className="py-2 pr-4 text-xs">{n.speed || "—"}</td>
                          <td className="py-2 text-xs">{n.dhcp || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hardware?.security_products && hardware.security_products.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
                Security Software
                {hardware.security_products.some((p) => p.type === "edr") && (
                  <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                    EDR detected
                  </span>
                )}
              </h3>
              <div className="flex flex-wrap gap-2">
                {hardware.security_products.map((p, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-lg border ${
                      p.type === "edr"
                        ? "border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300"
                        : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300"
                    }`}
                    title={p.source ? `Detected via ${p.source}` : undefined}
                  >
                    {p.type === "edr" ? "🛡️" : "🦠"} {p.name}
                    <span className="text-[10px] uppercase opacity-60">{p.type}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <WarrantyCard
            warranty={warranty}
            refreshing={warrantyRefreshing}
            onRefresh={refreshWarranty}
          />
        </div>
      )}

      {tab === "storage" && <StorageTab device={device} storage={storage} />}

      {tab === "software" && (
        <div>
          <input
            type="text"
            placeholder="Search software..."
            value={swSearch}
            onChange={(e) => setSwSearch(e.target.value)}
            className="mb-3 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={softwareSort.sort} onSort={softwareSort.toggle} />
                  <SortableTh label="Version" sortKey="version" sort={softwareSort.sort} onSort={softwareSort.toggle} />
                  <SortableTh label="Publisher" sortKey="publisher" sort={softwareSort.sort} onSort={softwareSort.toggle} />
                  <SortableTh label="Installed" sortKey="install_date" sort={softwareSort.sort} onSort={softwareSort.toggle} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(filteredSoftware, softwareSort.sort, (s, key) => {
                  switch (key) {
                    case "version":
                      return (s.version || "").toLowerCase();
                    case "publisher":
                      return (s.publisher || "").toLowerCase();
                    case "install_date":
                      return s.install_date ? Date.parse(s.install_date) || 0 : 0;
                    default:
                      return (s.name || "").toLowerCase();
                  }
                }).map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{s.name}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{s.version || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{s.publisher || "—"}</td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 text-xs">{s.install_date || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <UninstallButton deviceId={device.id} softwareId={s.id} name={s.name} />
                    </td>
                  </tr>
                ))}
                {filteredSoftware.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      No software found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "patches" && (
        <PatchesTab
          deviceId={device.id}
          patches={patches}
          summary={patchSummary}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          canManage={canAck}
          onAction={async (patchId, action) => {
            try {
              await api.post(`/api/v1/devices/${device.id}/patches/${patchId}/${action}`);
              const r = await api.get(`/api/v1/devices/${device.id}/patches`);
              setPatches(r.data);
              const s = await api.get(`/api/v1/devices/${device.id}/patches/summary`);
              setPatchSummary(s.data);
            } catch (err: any) {
              alert(err.response?.data?.detail || `Failed to ${action} patch`);
            }
          }}
        />
      )}

      {memberOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
              Rename / Domain — {device.hostname}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Currently:{" "}
              <span className="font-medium">
                {device.identity_join_type === "ad" || device.identity_join_type === "hybrid"
                  ? `joined to ${device.domain_name || "a domain"}`
                  : device.identity_join_type === "entra"
                    ? "Entra ID joined"
                    : "workgroup / standalone"}
              </span>
              . Changes apply on the next check-in and need a reboot to finish.
            </p>

            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 mb-4 text-xs">
              {(["rename", "join", "leave"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMemberMode(m)}
                  className={`px-3 py-1.5 rounded-md font-medium ${
                    memberMode === m
                      ? "bg-sky-600 text-white"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/40"
                  }`}
                >
                  {m === "rename" ? "Rename" : m === "join" ? "Join domain" : "Leave domain"}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {(memberMode === "rename" || memberMode === "join") && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    {memberMode === "join" ? "New computer name (optional)" : "New computer name"}
                  </label>
                  <input
                    type="text"
                    value={memberForm.new_name}
                    onChange={(e) => setMemberForm({ ...memberForm, new_name: e.target.value })}
                    placeholder="1-15 chars, letters/digits/hyphen"
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                  />
                </div>
              )}
              {memberMode === "join" && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Domain</label>
                    <input
                      type="text"
                      value={memberForm.domain}
                      onChange={(e) => setMemberForm({ ...memberForm, domain: e.target.value })}
                      placeholder="corp.example.com"
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                      Target OU (optional)
                    </label>
                    <input
                      type="text"
                      value={memberForm.ou_path}
                      onChange={(e) => setMemberForm({ ...memberForm, ou_path: e.target.value })}
                      placeholder="OU=Workstations,DC=corp,DC=example,DC=com"
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                    />
                  </div>
                </>
              )}
              {memberMode === "leave" && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Workgroup</label>
                  <input
                    type="text"
                    value={memberForm.workgroup}
                    onChange={(e) => setMemberForm({ ...memberForm, workgroup: e.target.value })}
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                  />
                </div>
              )}

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {memberMode === "join" ? (
                    "Domain credential with rights to join (required)."
                  ) : memberMode === "leave" ? (
                    "Domain credential to clean up the AD computer object (recommended)."
                  ) : renameNeedsCreds ? (
                    /* SPECIFIC TO THIS MACHINE, and stated as required.
                     *
                     * This read "For a domain-joined machine, a domain credential
                     * to update its AD object" — general advice, easy to take as
                     * optional. Three workstations were renamed with it left
                     * blank; each failed two seconds later with "Access is
                     * denied", and the operator spent a weekend waiting for a
                     * rename that had already failed. */
                    <span className="text-amber-700 dark:text-amber-400 font-medium">
                      {device.hostname} is joined to {device.domain_name || "a domain"}, so
                      renaming it also renames its computer object in Active Directory.
                      Credentials that may do that are <strong>required</strong> — without
                      them the domain refuses the rename.
                    </span>
                  ) : (
                    "Not needed — this machine is not joined to an Active Directory domain."
                  )}{" "}
                  The password is vault-encrypted and fetched by the agent only at run time — never stored in the clear.
                </p>
                <input
                  type="text"
                  value={memberForm.username}
                  onChange={(e) => setMemberForm({ ...memberForm, username: e.target.value })}
                  placeholder="DOMAIN\\admin or admin@corp.example.com"
                  autoComplete="off"
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                />
                <input
                  type="password"
                  value={memberForm.password}
                  onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })}
                  placeholder="Password"
                  autoComplete="new-password"
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={memberForm.reboot}
                  onChange={(e) => setMemberForm({ ...memberForm, reboot: e.target.checked })}
                />
                Reboot automatically to finish (~60s after applying)
              </label>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Type the current hostname (<code>{device.hostname}</code>) to confirm
                </label>
                <input
                  type="text"
                  value={memberForm.confirm_hostname}
                  onChange={(e) => setMemberForm({ ...memberForm, confirm_hostname: e.target.value })}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                  placeholder={device.hostname}
                />
              </div>

              {memberError && <p className="text-sm text-red-600 dark:text-red-400">{memberError}</p>}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setMemberOpen(false)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={submitMembership}
                disabled={memberBusy || memberBlocked}
                title={
                  memberBlocked
                    ? "Domain credentials are required to rename a domain-joined computer"
                    : undefined
                }
                className="text-sm px-4 py-2 rounded-lg bg-sky-600 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                {memberBusy
                  ? "Queuing…"
                  : memberMode === "rename"
                    ? "Rename computer"
                    : memberMode === "join"
                      ? "Join domain"
                      : "Leave domain"}
              </button>
            </div>
          </div>
        </div>
      )}

      {offboardOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">
              Uninstall agent — {device.hostname}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              The agent stops and removes its own Windows service, deletes the Programs &amp;
              Features entry, and removes its install folder on the next check-in (~60s). The
              device must be online to receive the command.
            </p>

            <div className="space-y-3">
              <div className="space-y-2">
                <label
                  className={`flex items-start gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                    offboardForm.mode === "uninstall"
                      ? "border-rose-400 bg-rose-50 dark:bg-rose-900/20"
                      : "border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-0.5"
                    checked={offboardForm.mode === "uninstall"}
                    onChange={() => setOffboardForm({ ...offboardForm, mode: "uninstall" })}
                  />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">Uninstall agent</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Removes the agent software. The device record stays (shown offline) so you
                      can reinstall later.
                    </span>
                  </span>
                </label>
                <label
                  className={`flex items-start gap-2 text-sm rounded-lg border px-3 py-2 cursor-pointer ${
                    offboardForm.mode === "factory_reset"
                      ? "border-rose-500 bg-rose-50 dark:bg-rose-900/20"
                      : "border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-0.5"
                    checked={offboardForm.mode === "factory_reset"}
                    onChange={() => setOffboardForm({ ...offboardForm, mode: "factory_reset" })}
                  />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Factory reset (full decommission)
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Uninstalls the agent, clears its scheduled tasks, and{" "}
                      <strong>permanently deletes this device record</strong> and its history once
                      the agent stops reporting.
                    </span>
                  </span>
                </label>
              </div>

              {offboardForm.mode === "factory_reset" && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={offboardForm.remove_gpo}
                      onChange={(e) => setOffboardForm({ ...offboardForm, remove_gpo: e.target.checked })}
                    />
                    Also remove the deployment GPO (Domain Controllers only)
                  </label>
                  {offboardForm.remove_gpo && (
                    <input
                      type="text"
                      value={offboardForm.gpo_name}
                      onChange={(e) => setOffboardForm({ ...offboardForm, gpo_name: e.target.value })}
                      placeholder="GPO name (e.g. AeroGauge Agent Deployment)"
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                    />
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Type the hostname (<code>{device.hostname}</code>) to confirm
                </label>
                <input
                  type="text"
                  value={offboardForm.confirm_hostname}
                  onChange={(e) => setOffboardForm({ ...offboardForm, confirm_hostname: e.target.value })}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                  placeholder={device.hostname}
                />
              </div>

              {offboardError && <p className="text-sm text-red-600 dark:text-red-400">{offboardError}</p>}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setOffboardOpen(false)}
                className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={submitOffboard}
                disabled={offboardBusy}
                className="text-sm px-4 py-2 rounded-lg bg-rose-600 text-white font-medium hover:bg-rose-700 disabled:opacity-50"
              >
                {offboardBusy
                  ? "Queuing…"
                  : offboardForm.mode === "factory_reset"
                    ? "Factory reset"
                    : "Uninstall agent"}
              </button>
            </div>
          </div>
        </div>
      )}

      {rebootOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">Reboot {device.hostname}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              The agent will execute <code>shutdown /r</code> on next check-in. Any logged-in user
              will see the message + countdown.
              {isServer && (
                <span className="block mt-2 text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded px-2 py-1">
                  ⚠ This is a <strong>server</strong>. Type the hostname below to confirm.
                </span>
              )}
            </p>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/40 border dark:border-gray-700 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={rebootForm.scheduled}
                  onChange={(e) => setRebootForm({ ...rebootForm, scheduled: e.target.checked })}
                />
                Schedule for later (instead of on next check-in)
              </label>
              {rebootForm.scheduled && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    Date &amp; time
                  </label>
                  <input
                    type="datetime-local"
                    value={rebootForm.schedule_at}
                    onChange={(e) =>
                      setRebootForm({ ...rebootForm, schedule_at: e.target.value })
                    }
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Uses your local time. Auto-enters maintenance mode around the reboot.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                  {rebootForm.scheduled ? "Warning countdown before reboot" : "Delay"}
                </label>
                <select
                  value={rebootForm.delay_seconds}
                  onChange={(e) =>
                    setRebootForm({ ...rebootForm, delay_seconds: Number(e.target.value) })
                  }
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                >
                  <option value={0}>Immediate</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes</option>
                  <option value={900}>15 minutes</option>
                  <option value={1800}>30 minutes</option>
                  <option value={3600}>1 hour</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                  Message shown to logged-in user
                </label>
                <input
                  value={rebootForm.message}
                  onChange={(e) => setRebootForm({ ...rebootForm, message: e.target.value })}
                  maxLength={512}
                  className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={rebootForm.force}
                  onChange={(e) => setRebootForm({ ...rebootForm, force: e.target.checked })}
                />
                Force-close open apps without prompting (<code>/f</code>)
              </label>
              {isServer && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
                    Type <code>{device.hostname}</code> to confirm
                  </label>
                  <input
                    value={rebootForm.confirm_hostname}
                    onChange={(e) =>
                      setRebootForm({ ...rebootForm, confirm_hostname: e.target.value })
                    }
                    placeholder={device.hostname}
                    autoComplete="off"
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                  />
                </div>
              )}
              {rebootError && (
                <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded">{rebootError}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => {
                  setRebootOpen(false);
                  setRebootError("");
                }}
                className="border dark:border-gray-600 text-sm px-4 py-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <ActionButton onAction={() => submitReboot()} variant="raw" className="bg-orange-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-orange-700" pendingLabel="Requesting…">
                {rebootForm.scheduled ? "Schedule Reboot" : "Queue Reboot"}
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiskUsageExplorer({ deviceId, drives }: { deviceId: number; drives: string[] }) {
  type Entry = { name: string; path: string; is_dir: boolean; size: number };
  // Normalize a drive letter ("C:" / "C") to a root path ("C:\").
  const toRoot = (d: string) => {
    const l = d.replace(/[\\/]+$/, "");
    return (l.endsWith(":") ? l : `${l}:`) + "\\";
  };
  const driveRoots = drives.length ? drives.map(toRoot) : ["C:\\"];
  const [path, setPath] = React.useState(driveRoots[0]);
  const [rows, setRows] = React.useState<Entry[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const pollRef = React.useRef<number | null>(null);

  const fmt = (b: number) => {
    if (b >= 1024 ** 4) return `${(b / 1024 ** 4).toFixed(2)} TB`;
    if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`;
    if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${b} B`;
  };

  const scan = React.useCallback(
    (target: string) => {
      setLoading(true);
      setError("");
      setPath(target);
      if (pollRef.current) window.clearTimeout(pollRef.current);
      api
        .post(`/api/v1/devices/${deviceId}/disk-usage`, { path: target })
        .then((r) => {
          const commandId = r.data.command_id;
          const started = Date.now();
          // POLL FAST FIRST, THEN BACK OFF. Every poll was scheduled 3s out,
          // including the FIRST one, so the fastest possible result took 3
          // seconds to appear no matter how quickly the device answered.
          // Measured end to end, the device now returns a folder scan in
          // about 1.3s (0.9s to pick the command up, ~0.4s to scan), so that
          // fixed 3s delay had become most of the wait the user sees.
          //
          // Backs off to the old 3s for genuinely long scans, so a slow
          // C:\ on a file server does not hammer the API for five minutes.
          let attempt = 0;
          const nextDelay = () => {
            attempt += 1;
            if (attempt <= 4) return 400; // first ~1.6s: the common case
            if (attempt <= 10) return 1000;
            return 3000;
          };
          const poll = () => {
            api
              .get(`/api/v1/devices/${deviceId}/command/${commandId}`)
              .then((cr) => {
                const st = cr.data?.status;
                if (st === "completed") {
                  let parsed: any = [];
                  try {
                    parsed = JSON.parse(cr.data?.result?.stdout || "[]");
                  } catch {
                    parsed = [];
                  }
                  if (!Array.isArray(parsed)) parsed = parsed ? [parsed] : [];
                  setRows(parsed);
                  setLoading(false);
                } else if (st === "failed") {
                  setError(cr.data?.error_message || "Scan failed on the device.");
                  setLoading(false);
                } else if (Date.now() - started > 300000) {
                  setError("Scan timed out — the folder may be very large or the device busy.");
                  setLoading(false);
                } else {
                  pollRef.current = window.setTimeout(poll, nextDelay());
                }
              })
              .catch(() => {
                pollRef.current = window.setTimeout(poll, nextDelay());
              });
          };
          pollRef.current = window.setTimeout(poll, nextDelay());
        })
        .catch((e) => {
          setError(e.response?.data?.detail || "Couldn't start the scan.");
          setLoading(false);
        });
    },
    [deviceId],
  );

  React.useEffect(() => {
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, []);

  const parent = (p: string) => {
    const trimmed = p.replace(/\\+$/, "");
    const idx = trimmed.lastIndexOf("\\");
    if (idx <= 2) return trimmed.slice(0, 3); // back to drive root "C:\"
    return trimmed.slice(0, idx);
  };
  const isDriveRoot = (p: string) => /^[A-Za-z]:\\?$/.test(p.replace(/\\+$/, "\\"));
  // Clickable path segments: C:\Users\foo -> [C:\, C:\Users, C:\Users\foo].
  const crumbs = () => {
    const parts = path.replace(/\\+$/, "").split("\\").filter(Boolean);
    return parts.map((seg, i) => ({
      label: seg,
      full: parts.slice(0, i + 1).join("\\") + (i === 0 ? "\\" : ""),
    }));
  };
  const maxSize = rows && rows.length ? Math.max(...rows.map((r) => r.size || 0), 1) : 1;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Disk usage</span>
        <select
          value={driveRoots.includes(path) ? path : ""}
          onChange={(e) => e.target.value && scan(e.target.value)}
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm"
          title="Pick a drive to scan"
        >
          {driveRoots.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          {!driveRoots.includes(path) && <option value="">(current path)</option>}
        </select>
        <input
          value={path}
          onChange={(e) => setPath(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && scan(path)}
          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm font-mono w-64"
          placeholder="C:\\"
          title="Current path (edit to scan a specific folder)"
        />
        <button
          onClick={() => scan(path)}
          disabled={loading}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Scanning…" : "Scan"}
        </button>
        {rows && (
          <button
            onClick={() => scan(parent(path))}
            disabled={loading}
            className="text-xs border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
          >
            ↑ Up
          </button>
        )}
      </div>
      {/* Clickable breadcrumb — jump back to any parent level. */}
      {rows && (
        <div className="flex items-center gap-1 flex-wrap mb-3 text-xs text-gray-500 dark:text-gray-400">
          {crumbs().map((c, i) => (
            <React.Fragment key={c.full}>
              {i > 0 && <span className="text-gray-300 dark:text-gray-600">›</span>}
              <button
                onClick={() => scan(c.full)}
                disabled={loading}
                className="hover:text-blue-600 dark:hover:text-blue-400 hover:underline disabled:opacity-50 font-mono"
                title={`Go to ${c.full}`}
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {loading && !rows && (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
          Sizing folders under <code className="font-mono">{path}</code>… (can take a minute on a
          large drive)
        </p>
      )}
      {/* ".." — go up a level, always available unless we're at a drive root. */}
      {rows && !isDriveRoot(path) && (
        <button
          onClick={() => scan(parent(path))}
          disabled={loading}
          className="flex items-center gap-2 text-sm text-blue-600 hover:underline mb-1 disabled:opacity-50"
          title={`Up to ${parent(path)}`}
        >
          📁 <span className="font-mono">..</span>
          <span className="text-xs text-gray-400">(up one level)</span>
        </button>
      )}
      {rows && rows.length === 0 && !loading && (
        <p className="text-sm text-gray-400 py-6 text-center">Nothing found under this path.</p>
      )}
      {rows && rows.length > 0 && (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className="w-56 truncate">
                {r.is_dir ? (
                  <button
                    onClick={() => scan(r.path)}
                    className="text-blue-600 hover:underline text-left truncate"
                    title={r.path}
                  >
                    📁 {r.name}
                  </button>
                ) : (
                  <span className="text-gray-700 dark:text-gray-300" title={r.path}>
                    📄 {r.name}
                  </span>
                )}
              </div>
              <div className="flex-1 bg-gray-100 dark:bg-gray-700/50 rounded h-3 overflow-hidden">
                <div
                  className="bg-blue-500 h-3"
                  style={{ width: `${Math.max(2, ((r.size || 0) / maxSize) * 100)}%` }}
                />
              </div>
              <div className="w-24 text-right font-mono text-xs text-gray-600 dark:text-gray-300">
                {fmt(r.size || 0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StorageTab({ device, storage }: { device: Device; storage: StorageReport | null }) {
  const [copied, setCopied] = React.useState<string | null>(null);
  const localSort = useSort("letter");
  const mappedSort = useSort("letter");
  const sharedSort = useSort("name");

  const [stoppingShare, setStoppingShare] = React.useState<string | null>(null);
  const [shareMsg, setShareMsg] = React.useState<string>("");

  const copyUnc = (path: string) => {
    navigator.clipboard?.writeText(path);
    setCopied(path);
    setTimeout(() => setCopied(null), 1800);
  };

  // Stop sharing a folder. Queued to the agent, so the row stays until the next
  // inventory cycle confirms the share is gone — say so rather than optimistically
  // removing the row and leaving the operator unsure whether it worked.
  const stopSharing = async (name: string) => {
    if (
      !window.confirm(
        `Stop sharing "${name}" on ${device.hostname}?\n\n` +
          "The folder and its contents are NOT deleted — only the network share is " +
          "removed. Anyone currently connected to it will lose access.",
      )
    )
      return;
    setStoppingShare(name);
    setShareMsg("");
    try {
      await api.post(
        `/api/v1/devices/${device.id}/shares/${encodeURIComponent(name)}/stop`,
      );
      setShareMsg(
        `Queued: stop sharing "${name}". The list refreshes after the device's next inventory.`,
      );
    } catch (e: any) {
      setShareMsg(e?.response?.data?.detail || "Couldn't stop the share.");
    } finally {
      setStoppingShare(null);
    }
  };

  const usagePct = (d: { total_gb?: number; free_gb?: number }) => {
    if (!d.total_gb || !d.free_gb) return null;
    return Math.round(((d.total_gb - d.free_gb) / d.total_gb) * 100);
  };

  const empty =
    !storage ||
    ((storage.local_drives || []).length === 0 &&
      (storage.mapped_drives || []).length === 0 &&
      (storage.shared_folders || []).length === 0);

  return (
    <div className="space-y-4">
      {empty && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center text-gray-400 text-sm">
          No storage info reported yet. The agent collects this on its hourly inventory check-in.
        </div>
      )}

      {device.status === "online" && (
        <DiskUsageExplorer
          deviceId={device.id}
          drives={(storage?.local_drives || []).map((d) => d.letter).filter(Boolean)}
        />
      )}

      {storage && storage.local_drives.length > 0 && (
        <Section title="Local Drives" count={storage.local_drives.length}>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Drive" sortKey="letter" sort={localSort.sort} onSort={localSort.toggle} />
                <SortableTh label="Label" sortKey="label" sort={localSort.sort} onSort={localSort.toggle} />
                <SortableTh label="FS" sortKey="file_system" sort={localSort.sort} onSort={localSort.toggle} />
                <SortableTh label="Type" sortKey="drive_type" sort={localSort.sort} onSort={localSort.toggle} />
                <SortableTh label="Used" sortKey="used" sort={localSort.sort} onSort={localSort.toggle} align="right" />
                <SortableTh label="Free" sortKey="free_gb" sort={localSort.sort} onSort={localSort.toggle} align="right" />
                <SortableTh label="Total" sortKey="total_gb" sort={localSort.sort} onSort={localSort.toggle} align="right" />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(storage.local_drives, localSort.sort, (d, key) => {
                switch (key) {
                  case "label":
                    return (d.label || "").toLowerCase();
                  case "file_system":
                    return (d.file_system || "").toLowerCase();
                  case "drive_type":
                    return (d.drive_type || "").toLowerCase();
                  case "used":
                    return usagePct(d) ?? -1;
                  case "free_gb":
                    return d.free_gb ?? 0;
                  case "total_gb":
                    return d.total_gb ?? 0;
                  default:
                    return (d.letter || "").toLowerCase();
                }
              }).map((d) => {
                const adminShare = `\\\\${device.hostname}\\${d.letter}$`;
                const pct = usagePct(d);
                return (
                  <tr key={d.letter}>
                    <td className="px-3 py-2 font-mono">{d.letter}:</td>
                    <td className="px-3 py-2">{d.label || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.file_system || "—"}</td>
                    <td className="px-3 py-2 text-xs">{d.drive_type || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      {pct != null ? (
                        <span
                          className={
                            pct > 90
                              ? "text-red-600 font-semibold"
                              : pct > 75
                                ? "text-orange-600 dark:text-orange-400"
                                : "text-gray-700 dark:text-gray-300"
                          }
                        >
                          {pct}%
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {d.free_gb != null ? `${d.free_gb} GB` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {d.total_gb != null ? `${d.total_gb} GB` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => copyUnc(adminShare)}
                        className="text-xs text-blue-600 hover:underline"
                        title={`Copy ${adminShare} for pasting into Run / Explorer`}
                      >
                        {copied === adminShare ? "Copied!" : "Copy admin path"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-2 px-3">
            Admin shares (<span className="font-mono">\\hostname\C$</span>) require Windows admin
            credentials + network reachability.
          </p>
        </Section>
      )}

      {storage && storage.mapped_drives.length > 0 && (
        <Section title="Mapped Network Drives" count={storage.mapped_drives.length}>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Drive" sortKey="letter" sort={mappedSort.sort} onSort={mappedSort.toggle} />
                <SortableTh label="Path" sortKey="remote_path" sort={mappedSort.sort} onSort={mappedSort.toggle} />
                <SortableTh label="User" sortKey="username" sort={mappedSort.sort} onSort={mappedSort.toggle} />
                <SortableTh label="Persistent" sortKey="persistent" sort={mappedSort.sort} onSort={mappedSort.toggle} />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(storage.mapped_drives, mappedSort.sort, (m, key) => {
                switch (key) {
                  case "remote_path":
                    return (m.remote_path || "").toLowerCase();
                  case "username":
                    return (m.username || "").toLowerCase();
                  case "persistent":
                    return m.persistent ? 1 : 0;
                  default:
                    return (m.letter || "").toLowerCase();
                }
              }).map((m) => (
                <tr key={m.letter}>
                  <td className="px-3 py-2 font-mono">{m.letter}:</td>
                  <td className="px-3 py-2 font-mono text-xs">{m.remote_path}</td>
                  <td className="px-3 py-2 text-xs">{m.username || "—"}</td>
                  <td className="px-3 py-2 text-xs">{m.persistent ? "Yes" : "No"}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => copyUnc(m.remote_path)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {copied === m.remote_path ? "Copied!" : "Copy path"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {storage && storage.shared_folders.length > 0 && (
        <Section
          title="Shared Folders (this device is hosting)"
          count={storage.shared_folders.length}
        >
          {shareMsg && (
            <p className="text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 mb-2">
              {shareMsg}
            </p>
          )}
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Share" sortKey="name" sort={sharedSort.sort} onSort={sharedSort.toggle} />
                <SortableTh label="Local Path" sortKey="path" sort={sharedSort.sort} onSort={sharedSort.toggle} />
                <SortableTh label="Description" sortKey="description" sort={sharedSort.sort} onSort={sharedSort.toggle} />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(storage.shared_folders, sharedSort.sort, (s, key) => {
                switch (key) {
                  case "path":
                    return (s.path || "").toLowerCase();
                  case "description":
                    return (s.description || "").toLowerCase();
                  default:
                    return (s.name || "").toLowerCase();
                }
              }).map((s) => {
                const unc = `\\\\${device.hostname}\\${s.name}`;
                const isAdminShare =
                  /^(ADMIN|IPC|PRINT)\$$/i.test(s.name) || /^[A-Za-z]\$$/.test(s.name);
                return (
                  <tr key={s.name} className="group hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-2 font-mono text-xs">{unc}</td>
                    <td className="px-3 py-2 text-xs">{s.path || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{s.description || "—"}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => copyUnc(unc)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {copied === unc ? "Copied!" : "Copy path"}
                      </button>
                      {/* Revealed on row hover so the destructive action isn't
                          sitting under the cursor during ordinary browsing, but
                          is one move away. Kept focusable so it is reachable by
                          keyboard, where there is no hover. */}
                      {!isAdminShare && (
                        <button
                          onClick={() => stopSharing(s.name)}
                          disabled={stoppingShare === s.name}
                          title="Stop sharing this folder (the folder and its contents are untouched)"
                          className="ml-3 text-xs text-red-600 dark:text-red-400 hover:underline
                                     opacity-0 group-hover:opacity-100 focus:opacity-100
                                     transition-opacity disabled:opacity-50"
                        >
                          {stoppingShare === s.name ? "Stopping…" : "Stop sharing"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {storage?.updated_at && (
        <p className="text-xs text-gray-400 px-2">
          Last reported {formatDateTime(storage.updated_at)}
        </p>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
      <div className="px-4 py-2 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
        <span className="text-xs text-gray-400">({count})</span>
      </div>
      {children}
    </div>
  );
}

function PatchesTab({
  deviceId: _deviceId,
  patches,
  summary,
  showHistory,
  setShowHistory,
  canManage,
  onAction,
}: {
  deviceId: number;
  patches: DevicePatch[];
  summary: DevicePatchSummary | null;
  showHistory: boolean;
  setShowHistory: (v: boolean) => void;
  canManage: boolean;
  onAction: (patchId: number, action: "approve" | "decline" | "reset") => void;
}) {
  const available = patches.filter((p) => p.status === "available");
  const installingNow = available.filter(
    (p) =>
      (p.approval_state === "approved" || p.approval_state === "auto_approved") &&
      !!p.install_command_id,
  );
  const approvedQueued = available.filter(
    (p) =>
      (p.approval_state === "approved" || p.approval_state === "auto_approved") &&
      !p.install_command_id,
  );
  const pending = available.filter(
    (p) =>
      p.approval_state === "pending" ||
      p.approval_state === "auto_declined" ||
      p.approval_state === "declined",
  );
  const installed = patches.filter((p) => p.status === "installed");
  const failed = patches.filter((p) => p.status === "failed");

  const approvedSort = useSort("severity", "desc");
  const pendingSort = useSort("severity", "desc");
  const historySort = useSort("install_date", "desc");
  const sevRank: Record<string, number> = {
    Critical: 4,
    Important: 3,
    Moderate: 2,
    Low: 1,
  };
  const approvalRank: Record<string, number> = {
    pending: 5,
    auto_declined: 4,
    declined: 3,
    auto_approved: 2,
    approved: 1,
  };

  const sevBadge = (sev: string) => {
    const cls =
      sev === "Critical"
        ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
        : sev === "Important"
          ? "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300"
          : sev === "Moderate"
            ? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
            : sev === "Low"
              ? "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
        {sev || "Unspecified"}
      </span>
    );
  };

  const sourceBadge = (source: string) => {
    const cls =
      source === "winget"
        ? "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
        : "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300";
    const label = source === "winget" ? "winget" : "WUA";
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${cls} mr-2`}>
        {label}
      </span>
    );
  };

  const approvalBadge = (state: string) => {
    const info: Record<string, { label: string; cls: string }> = {
      approved: {
        label: "Approved",
        cls: "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300",
      },
      auto_approved: {
        label: "Auto-approved",
        cls: "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800",
      },
      declined: {
        label: "Declined",
        cls: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300",
      },
      auto_declined: {
        label: "Auto-declined",
        cls: "bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800",
      },
      pending: {
        label: "Pending review",
        cls: "bg-yellow-50 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800",
      },
    };
    const i = info[state] || {
      label: state,
      cls: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300",
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${i.cls}`}>{i.label}</span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryStat
            label="Pending"
            value={summary?.pending ?? 0}
            color="text-gray-900 dark:text-gray-100"
          />
          <SummaryStat label="Critical" value={summary?.critical ?? 0} color="text-red-600" />
          <SummaryStat label="Important" value={summary?.important ?? 0} color="text-orange-600" />
          <SummaryStat
            label="Reboot needed"
            value={summary?.reboot_required ?? 0}
            color="text-orange-600"
          />
          <SummaryStat
            label="Installed (history)"
            value={summary?.installed ?? 0}
            color="text-gray-600 dark:text-gray-400"
          />
        </div>
      </div>

      {/* Installing now */}
      {installingNow.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 border-b dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-300">Installing now</h3>
            <span className="text-xs text-blue-500 dark:text-blue-400">{installingNow.length}</span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {installingNow.map((p) => (
              <li key={p.id} className="px-4 py-2 text-sm flex items-center justify-between">
                <span className="text-gray-800 dark:text-gray-200">
                  {sourceBadge(p.source)}
                  {p.title}
                </span>
                <span className="text-xs text-blue-600">cmd #{p.install_command_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Approved & queued for next maintenance window */}
      {approvedQueued.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-800 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 border-b dark:border-green-800 bg-green-50 dark:bg-green-900/30 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-green-700 dark:text-green-300">
              Approved — will install at next maintenance window
            </h3>
            <span className="text-xs text-green-500 dark:text-green-400">{approvedQueued.length}</span>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <SortableTh label="Severity" sortKey="severity" sort={approvedSort.sort} onSort={approvedSort.toggle} />
                <SortableTh label="Title" sortKey="title" sort={approvedSort.sort} onSort={approvedSort.toggle} />
                <SortableTh label="KB" sortKey="kb" sort={approvedSort.sort} onSort={approvedSort.toggle} />
                {canManage && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(approvedQueued, approvedSort.sort, (p, key) => {
                switch (key) {
                  case "title":
                    return (p.title || "").toLowerCase();
                  case "kb":
                    return (p.kb || "").toLowerCase();
                  default:
                    return sevRank[p.severity] ?? 0;
                }
              }).map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5">{sevBadge(p.severity)}</td>
                  <td className="px-4 py-2.5 text-gray-800 dark:text-gray-200">
                    {sourceBadge(p.source)}
                    {p.title}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{p.kb || "—"}</td>
                  {canManage && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={() => onAction(p.id, "decline")}
                        className="text-xs bg-red-600 text-white px-2 py-1 rounded mr-1 hover:bg-red-700"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => onAction(p.id, "reset")}
                        className="text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/40"
                      >
                        Reset
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending review */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <div className="px-4 py-2 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Pending review</h3>
          <span className="text-xs text-gray-400">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No pending updates. This device is current.
          </p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
              <tr>
                <SortableTh label="Severity" sortKey="severity" sort={pendingSort.sort} onSort={pendingSort.toggle} />
                <SortableTh label="Title" sortKey="title" sort={pendingSort.sort} onSort={pendingSort.toggle} />
                <SortableTh label="KB" sortKey="kb" sort={pendingSort.sort} onSort={pendingSort.toggle} />
                <SortableTh label="Approval" sortKey="approval_state" sort={pendingSort.sort} onSort={pendingSort.toggle} />
                <SortableTh label="Reboot" sortKey="reboot_required" sort={pendingSort.sort} onSort={pendingSort.toggle} />
                {canManage && <th className="px-4 py-2 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(pending, pendingSort.sort, (p, key) => {
                switch (key) {
                  case "title":
                    return (p.title || "").toLowerCase();
                  case "kb":
                    return (p.kb || "").toLowerCase();
                  case "approval_state":
                    return approvalRank[p.approval_state] ?? 0;
                  case "reboot_required":
                    return p.reboot_required ? 1 : 0;
                  default:
                    return sevRank[p.severity] ?? 0;
                }
              }).map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2.5">{sevBadge(p.severity)}</td>
                  <td className="px-4 py-2.5 text-gray-800 dark:text-gray-200">
                    {sourceBadge(p.source)}
                    {p.title}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {p.kb ? (
                      <a
                        href={`https://support.microsoft.com/help/${p.kb.replace(/^KB/i, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {p.kb}
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2.5">{approvalBadge(p.approval_state)}</td>
                  <td className="px-4 py-2.5 text-xs">{p.reboot_required ? "Yes" : "No"}</td>
                  {canManage && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {(p.approval_state === "pending" || p.approval_state === "auto_declined") && (
                        <button
                          onClick={() => onAction(p.id, "approve")}
                          className="text-xs bg-green-600 text-white px-2 py-1 rounded mr-1 hover:bg-green-700"
                        >
                          Approve
                        </button>
                      )}
                      {(p.approval_state === "pending" || p.approval_state === "auto_approved") && (
                        <button
                          onClick={() => onAction(p.id, "decline")}
                          className="text-xs bg-red-600 text-white px-2 py-1 rounded mr-1 hover:bg-red-700"
                        >
                          Decline
                        </button>
                      )}
                      {(p.approval_state === "approved" || p.approval_state === "declined") && (
                        <button
                          onClick={() => onAction(p.id, "reset")}
                          className="text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/40"
                        >
                          Reset
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Failed (with error detail) */}
      {failed.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-red-100 dark:border-red-900 shadow-sm overflow-x-auto">
          <div className="px-4 py-2 border-b dark:border-red-900 bg-red-50 dark:bg-red-900/30 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">Recent failures</h3>
            <span className="text-xs text-red-500 dark:text-red-400">{failed.length}</span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {failed.slice(0, 20).map((p) => (
              <li key={p.id} className="px-4 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-800 dark:text-gray-200">
                    {sourceBadge(p.source)}
                    {p.title}
                  </span>
                  {canManage && (
                    <button
                      onClick={() => onAction(p.id, "reset")}
                      className="text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700/40"
                    >
                      Reset to retry
                    </button>
                  )}
                </div>
                {p.error_message && (
                  <div className="text-xs text-red-600 mt-1 font-mono">{p.error_message}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Install history (collapsible) */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 flex items-center justify-between text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/40"
        >
          <span>Install history</span>
          <span className="text-xs text-gray-400">
            {installed.length} {showHistory ? "▼" : "▶"}
          </span>
        </button>
        {showHistory &&
          (installed.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              No install history recorded yet.
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                <tr>
                  <SortableTh label="Title" sortKey="title" sort={historySort.sort} onSort={historySort.toggle} />
                  <SortableTh label="Installed" sortKey="install_date" sort={historySort.sort} onSort={historySort.toggle} />
                  <th className="px-4 py-2 text-left">Reboot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(installed, historySort.sort, (p, key) =>
                  key === "install_date"
                    ? p.install_date
                      ? Date.parse(p.install_date) || 0
                      : 0
                    : (p.title || "").toLowerCase(),
                )
                  .slice(0, 100)
                  .map((p) => {
                  const needsReboot = p.reboot_required && !p.reboot_done_at;
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2 text-gray-800 dark:text-gray-200">
                        {sourceBadge(p.source)}
                        {p.title}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {p.install_date ? formatDateTime(p.install_date) : "—"}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {needsReboot ? (
                          <span className="bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full font-medium">
                            Reboot pending
                          </span>
                        ) : p.reboot_done_at ? (
                          <span className="text-green-700">
                            Rebooted{" "}
                            {formatRelative(p.reboot_done_at)}
                          </span>
                        ) : (
                          <span className="text-gray-400">Not required</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function WarrantyCard({
  warranty,
  refreshing,
  onRefresh,
}: {
  warranty: DeviceWarranty | null;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  // Days-remaining derived purely from end_date — null when we don't
  // know (unknown vendor, missing data, not synced yet).
  const daysLeft =
    warranty?.end_date != null
      ? Math.floor((utcMillis(warranty.end_date) - Date.now()) / (1000 * 60 * 60 * 24))
      : null;

  const statusPill =
    warranty == null
      ? { text: "Not synced", color: "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" }
      : warranty.status === "active"
        ? {
            text: daysLeft != null && daysLeft <= 60 ? `Expires in ${daysLeft}d` : "Active",
            color:
              daysLeft != null && daysLeft <= 30
                ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                : daysLeft != null && daysLeft <= 60
                  ? "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                  : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
          }
        : warranty.status === "expired"
          ? { text: "Expired", color: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300" }
          : { text: "Unknown", color: "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" };

  const vendorLabel: Record<string, string> = {
    dell: "Dell",
    hp: "HP",
    lenovo: "Lenovo",
    unknown: "Unknown",
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Warranty</h3>
          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${statusPill.color}`}>
            {statusPill.text}
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="text-xs border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {warranty == null ? (
        <p className="text-gray-400 text-sm">
          No warranty data cached yet. Click <strong>Refresh</strong> to look up this device against
          its vendor's API.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {[
              ["Vendor", vendorLabel[warranty.vendor] || warranty.vendor],
              ["Service tag", warranty.service_tag],
              ["Service level", warranty.service_level],
              [
                "Start date",
                warranty.start_date ? formatDate(warranty.start_date) : null,
              ],
              [
                "End date",
                warranty.end_date ? formatDate(warranty.end_date) : null,
              ],
              [
                "Last synced",
                warranty.last_synced_at
                  ? formatRelative(warranty.last_synced_at)
                  : "never",
              ],
            ].map(([label, value]) => (
              <div key={label as string}>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">{value || "—"}</div>
              </div>
            ))}
          </div>

          {warranty.last_sync_error && (
            <div className="mt-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs px-3 py-2 rounded">
              <strong>Last refresh failed:</strong> {warranty.last_sync_error}
              {warranty.last_synced_at && (
                <span className="ml-1 text-amber-700/70 dark:text-amber-400/70">
                  (showing data from {formatDate(warranty.last_synced_at)})
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UninstallButton({
  deviceId,
  softwareId,
  name,
}: {
  deviceId: number;
  softwareId: number;
  name: string;
}) {
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const onClick = async () => {
    if (
      !window.confirm(
        `Uninstall "${name}" from this device?\n\n` +
          "The agent will run a silent uninstall (winget first, then registry " +
          "UninstallString fallback). The row stays here until the next " +
          "inventory check-in confirms it's gone.",
      )
    )
      return;
    setBusy(true);
    try {
      await api.post(`/api/v1/devices/${deviceId}/software/${softwareId}/uninstall`);
      setDone(true);
    } catch (e: any) {
      alert("Uninstall failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <span className="text-xs text-amber-700 dark:text-amber-400 italic" title="Queued; pending agent check-in">
        queued
      </span>
    );
  }
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="text-xs px-2 py-1 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50"
      title="Queue a silent uninstall for this app"
    >
      Uninstall
    </button>
  );
}

// In-browser RDP via the Devolutions Gateway, tunneled through the agent. One
// click: prompt creds → open a session (ephemeral bridge port + agent relay) →
// wait for the agent → mint a Gateway WebApp token server-side → hand it + the
// auto-connect config to a same-origin launch page (rdp-launch.html) that seeds
// the Gateway session and redirects. No VPN, NO separate Gateway login.
function RemoteDesktopButton({ deviceId }: { deviceId: number }) {
  const [open, setOpen] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState("");

  const connect = async () => {
    if (!username || !password) {
      setStatus("Enter username and password.");
      return;
    }
    // Open the same-origin launch page now (inside the click → popup-safe). Its
    // real RMM origin means the Gateway session we seed there persists across the
    // redirect, so the Gateway client opens already authenticated — no login.
    const win = window.open(`${window.location.origin}/rdp-launch.html`, "aerogauge-rdp");
    if (!win) {
      setStatus("Pop-up blocked — allow pop-ups for this site, then click Connect again.");
      return;
    }
    win.focus();
    setBusy(true);
    setStatus("Preparing… connecting to the agent (~20s)…");
    try {
      const r = await api.post(`/api/v1/shell/rdp-sessions`, { device_id: deviceId });
      const { bridge_port, command_id, token: sessionToken } = r.data;

      // Wait until the agent has picked up the relay (open_tcp_relay -> completed),
      // so the Gateway connecting to the bridge pairs immediately.
      let ready = false;
      for (let i = 0; i < 30; i++) {
        await new Promise((res) => setTimeout(res, 1500));
        try {
          const s = await api.get(`/api/v1/shell/rdp-sessions/command/${command_id}`);
          if (s.data.status === "completed") {
            ready = true;
            break;
          }
          if (s.data.status === "failed") throw new Error(s.data.error_message || "relay failed");
        } catch {
          /* keep polling */
        }
      }
      if (!ready) throw new Error("agent did not become ready (is it online?)");

      // Mint a Gateway WebApp token (server-side) for auto-auth + the base64 .rdp
      // config for the client's autoconnect.
      const t = await api.get(`/api/v1/shell/gateway-webapp-token`);
      const expires = new Date(Date.now() + (t.data.lifetime - 120) * 1000).toISOString();
      const session = { username: t.data.username, token: t.data.token, expires };
      const rdp =
        `full address:s:127.0.0.1\r\n` +
        `server port:i:${bridge_port}\r\n` +
        `username:s:${username}\r\n` +
        `ClearTextPassword:s:${password}\r\n`;
      const url =
        `${window.location.origin}/jet/webapp/client` +
        `?config=${encodeURIComponent(btoa(rdp))}&autoconnect=true&protocol=rdp`;

      // Hand the session + URL to the launch page; it seeds sessionStorage (real
      // origin) then redirects → Gateway opens authenticated → auto-connects.
      const origin = window.location.origin;
      const payload = { type: "ag-rdp-launch", session, url, sessionToken };
      win.postMessage(payload, origin);
      let tries = 0;
      const iv = window.setInterval(() => {
        try {
          win.postMessage(payload, origin);
        } catch {
          /* ignore */
        }
        if (++tries >= 6) window.clearInterval(iv);
      }, 500);

      // NOTE: the launch page (rdp-launch.html) hosts the client in an iframe and
      // does the close-on-disconnect poll itself — in that foreground tab, so it
      // isn't throttled, and the RMM tab does no polling (stays responsive).
      setOpen(false);
      setPassword("");
      setStatus("");
    } catch (e: any) {
      if (win) win.close();
      setStatus(e?.response?.data?.detail || e?.message || "Remote desktop failed to start");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => {
          setStatus("");
          setOpen(true);
        }}
        className="text-xs bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-emerald-700"
        title="Open an in-browser Remote Desktop session (via the Gateway, tunneled through the agent — no VPN)"
      >
        🖥 Remote Desktop
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-gray-100">Remote Desktop</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Sign-in for the target machine. Opens the desktop in a new tab, tunneled through the
              agent — no VPN, no separate gateway login.
            </p>
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Username</label>
            <input
              type="text"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="DOMAIN\user"
              autoComplete="off"
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mb-3 font-mono"
            />
            <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 block mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !busy) connect();
              }}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mb-3 font-mono"
            />
            {status && <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">{status}</div>}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setOpen(false);
                  setPassword("");
                }}
                disabled={busy}
                className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-4 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/40 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={connect}
                disabled={busy || !username || !password}
                className="text-sm bg-emerald-600 text-white px-4 py-1.5 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy ? "Connecting…" : "Connect"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type FileXfer = {
  id: number;
  direction: string;
  filename: string;
  remote_path: string;
  size_bytes: number | null;
  status: string;
  error: string | null;
  created_at: string | null;
  downloadable: boolean;
};

function FilesTab({ deviceId }: { deviceId: number }) {
  const [rows, setRows] = React.useState<FileXfer[]>([]);
  const [pushPath, setPushPath] = React.useState("");
  const [pullPath, setPullPath] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [err, setErr] = React.useState("");
  const [browse, setBrowse] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(() => {
    api
      .get(`/api/v1/devices/${deviceId}/files`)
      .then((r) => setRows(r.data || []))
      .catch(() => {});
  }, [deviceId]);

  React.useEffect(() => {
    load();
    const t = setInterval(load, 5000); // poll so status flips as the agent acts
    return () => clearInterval(t);
  }, [load]);

  const fmt = (b: number | null) => {
    if (!b) return "—";
    if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
    if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${b} B`;
  };

  const doPush = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || !pushPath.trim()) {
      setErr("Choose a file and enter a destination path on the device.");
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("remote_path", pushPath.trim());
      await api.post(`/api/v1/devices/${deviceId}/files/push`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Sending ${f.name} to the device…`);
      setPushPath("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Push failed");
    } finally {
      setBusy(false);
    }
  };

  const doPull = async (pathArg?: string) => {
    const p = (typeof pathArg === "string" ? pathArg : pullPath).trim();
    if (!p) {
      setErr("Enter the path of the file to fetch, or click Browse to pick one.");
      return;
    }
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await api.post(`/api/v1/devices/${deviceId}/files/pull`, { path: p });
      setMsg(`Fetching ${p} from the device…`);
      setPullPath("");
      load();
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Pull failed");
    } finally {
      setBusy(false);
    }
  };

  const download = (t: FileXfer) => {
    api
      .get(`/api/v1/devices/files/${t.id}/download`, { responseType: "blob" })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = t.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setErr("Download failed"));
  };

  const statusColor = (s: string) =>
    s === "completed" || s === "uploaded"
      ? "text-green-600"
      : s === "failed"
        ? "text-red-600"
        : "text-amber-600";

  return (
    <div className="space-y-4">
      {msg && <p className="text-sm text-green-700 dark:text-green-400">{msg}</p>}
      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Send a file to the device
          </h3>
          <input ref={fileRef} type="file" className="text-sm mb-2 block" />
          <input
            value={pushPath}
            onChange={(e) => setPushPath(e.target.value)}
            placeholder="Destination, e.g. C:\Temp\ or C:\Temp\file.txt"
            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm font-mono mb-2"
          />
          <button
            onClick={doPush}
            disabled={busy}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Send to device
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Fetch a file from the device
          </h3>
          <div className="flex gap-2 mb-2">
            <input
              value={pullPath}
              onChange={(e) => setPullPath(e.target.value)}
              placeholder="Path on device, or click Browse →"
              className="flex-1 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm font-mono"
            />
            <button
              onClick={() => setBrowse(true)}
              className="text-sm border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 whitespace-nowrap"
              title="Browse the device and pick a file"
            >
              📂 Browse…
            </button>
          </div>
          <button
            onClick={() => doPull()}
            disabled={busy}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Fetch from device
          </button>
          <p className="text-xs text-gray-400 mt-2">Up to 100 MB. Runs on the device's next check-in.</p>
        </div>
      </div>

      {browse && (
        <DeviceFileBrowser
          deviceId={deviceId}
          onClose={() => setBrowse(false)}
          onPick={(p) => {
            setBrowse(false);
            setPullPath(p);
            doPull(p);
          }}
        />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Dir</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">File / path</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Size</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  No transfers yet.
                </td>
              </tr>
            ) : (
              rows.map((t) => (
                <tr key={t.id} className="text-gray-800 dark:text-gray-100">
                  <td className="px-4 py-2">{t.direction === "push" ? "⬇ to device" : "⬆ from device"}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium">{t.filename}</div>
                    <div className="text-xs text-gray-400 font-mono">{t.remote_path}</div>
                  </td>
                  <td className="px-4 py-2">{fmt(t.size_bytes)}</td>
                  <td className="px-4 py-2">
                    <span className={`font-medium ${statusColor(t.status)}`}>{t.status}</span>
                    {t.error && <div className="text-xs text-red-500">{t.error}</div>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {t.downloadable && (
                      <button onClick={() => download(t)} className="text-xs text-blue-600 hover:underline">
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type WinService = {
  name: string;
  display_name: string | null;
  state: string | null;
  start_mode: string | null;
  status: string | null;
};

function ServicesTab({
  deviceId,
  online,
  canManage,
}: {
  deviceId: number;
  online: boolean;
  canManage: boolean;
}) {
  const [services, setServices] = React.useState<WinService[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [busySvc, setBusySvc] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState("");
  const pollRef = React.useRef<number | null>(null);

  // Queue a device command, then poll its result until completed/failed.
  const runCommand = React.useCallback(
    async (
      queue: () => Promise<{ data: { command_id: number } }>,
      onDone: (result: any) => void,
    ) => {
      setError("");
      if (pollRef.current) {
        window.clearTimeout(pollRef.current);
        pollRef.current = null;
      }
      let commandId: number;
      try {
        const resp = await queue();
        commandId = resp.data.command_id;
      } catch (e: any) {
        setError(e.response?.data?.detail || "Failed to queue the command.");
        setLoading(false);
        setBusySvc(null);
        return;
      }
      const startedAt = Date.now();
      const poll = async () => {
        pollRef.current = null;
        let status: string | undefined;
        let result: any;
        let errorMessage: string | undefined;
        try {
          const r = await api.get(`/api/v1/devices/${deviceId}/command/${commandId}`);
          status = r.data?.status;
          result = r.data?.result;
          errorMessage = r.data?.error_message;
        } catch {
          /* transient — keep polling */
        }
        if (status === "completed") {
          onDone(result);
          return;
        }
        if (status === "failed") {
          setError(errorMessage || "The device reported a failure.");
          setLoading(false);
          setBusySvc(null);
          return;
        }
        if (Date.now() - startedAt > 90000) {
          setError(
            online
              ? "Timed out waiting for the device. It may be busy — try again."
              : "Device is offline — it will run this when it next checks in.",
          );
          setLoading(false);
          setBusySvc(null);
          return;
        }
        pollRef.current = window.setTimeout(poll, 2500);
      };
      pollRef.current = window.setTimeout(poll, 2000);
    },
    [deviceId, online],
  );

  const loadServices = React.useCallback(() => {
    setLoading(true);
    setNotice("");
    runCommand(
      () => api.post(`/api/v1/devices/${deviceId}/services/list`),
      (result) => {
        const list: WinService[] = (result?.services || []).slice();
        list.sort((a, b) =>
          (a.display_name || a.name || "").localeCompare(b.display_name || b.name || ""),
        );
        setServices(list);
        setLoading(false);
      },
    );
  }, [deviceId, runCommand]);

  // Show the last cached list instantly (no ~20s device round-trip), then
  // kick off a fresh live scan in the background.
  const loadCached = React.useCallback(() => {
    api
      .get(`/api/v1/devices/${deviceId}/services/cached`)
      .then((r) => {
        const list: WinService[] = (r.data?.services || []).slice();
        if (list.length) {
          list.sort((a, b) =>
            (a.display_name || a.name || "").localeCompare(b.display_name || b.name || ""),
          );
          setServices(list);
        }
      })
      .catch(() => {});
  }, [deviceId]);

  React.useEffect(() => {
    loadCached();
    loadServices();
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [loadCached, loadServices]);

  const control = (svc: WinService, action: string, startType?: string) => {
    const verb =
      action === "set_start_type" ? `set ${svc.display_name || svc.name} startup to ${startType}` : `${action} ${svc.display_name || svc.name}`;
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    setBusySvc(svc.name);
    setNotice("");
    runCommand(
      () =>
        api.post(`/api/v1/devices/${deviceId}/services/control`, {
          service_name: svc.name,
          action,
          start_type: startType,
        }),
      () => {
        setNotice(`Done: ${verb}. Refreshing…`);
        setBusySvc(null);
        loadServices();
      },
    );
  };

  // Sortable, like the software and BitLocker tables on this page already are.
  // A server can report 200+ services; without sorting, finding one meant
  // scrolling or knowing its exact name to search for.
  const svcSort = useSort("display_name");

  const searched = (services || []).filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.display_name || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q)
    );
  });

  // Sort AFTER filtering so the visible rows are what's ordered. Service state
  // sorts semantically (running first), not alphabetically — "running" before
  // "stopped" is what an operator scanning for problems actually wants.
  const filtered = sortRows(searched, svcSort.sort, (row, key) => {
    switch (key) {
      case "display_name":
        return (row.display_name || row.name || "").toLowerCase();
      case "state":
        return (row.state || "").toLowerCase() === "running" ? 0 : 1;
      case "start_mode":
        return (row.start_mode || "").toLowerCase();
      default:
        return "";
    }
  });

  const stateBadge = (state: string | null) => {
    const running = (state || "").toLowerCase() === "running";
    return (
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${running ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
      >
        {state || "—"}
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter services…"
            className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-56"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {services ? `${filtered.length} of ${services.length}` : ""}
          </span>
        </div>
        <button
          onClick={loadServices}
          disabled={loading}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>

      {notice && <p className="text-xs text-green-700 dark:text-green-400 mb-2">{notice}</p>}
      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}

      {loading && !services ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
          Fetching services from the device…
        </p>
      ) : !services || services.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
          {error ? "" : "No services reported."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
              <tr>
                <SortableTh
                  label="Service"
                  sortKey="display_name"
                  sort={svcSort.sort}
                  onSort={svcSort.toggle}
                />
                <SortableTh
                  label="State"
                  sortKey="state"
                  sort={svcSort.sort}
                  onSort={svcSort.toggle}
                />
                <SortableTh
                  label="Startup"
                  sortKey="start_mode"
                  sort={svcSort.sort}
                  onSort={svcSort.toggle}
                />
                {canManage && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((s) => {
                const running = (s.state || "").toLowerCase() === "running";
                const rowBusy = busySvc === s.name;
                return (
                  <tr key={s.name} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {s.display_name || s.name}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">{s.name}</div>
                    </td>
                    <td className="px-3 py-2">{stateBadge(s.state)}</td>
                    <td className="px-3 py-2">
                      {canManage ? (
                        <select
                          value={
                            (s.start_mode || "").toLowerCase().startsWith("auto")
                              ? "auto"
                              : (s.start_mode || "").toLowerCase() === "disabled"
                                ? "disabled"
                                : "manual"
                          }
                          disabled={rowBusy}
                          onChange={(e) => control(s, "set_start_type", e.target.value)}
                          className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-xs"
                        >
                          <option value="auto">Automatic</option>
                          <option value="manual">Manual</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      ) : (
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {s.start_mode || "—"}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {running ? (
                            <button
                              onClick={() => control(s, "stop")}
                              disabled={rowBusy}
                              className="text-xs text-red-600 hover:underline disabled:opacity-50"
                            >
                              Stop
                            </button>
                          ) : (
                            <button
                              onClick={() => control(s, "start")}
                              disabled={rowBusy}
                              className="text-xs text-green-600 hover:underline disabled:opacity-50"
                            >
                              Start
                            </button>
                          )}
                          <button
                            onClick={() => control(s, "restart")}
                            disabled={rowBusy}
                            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                          >
                            Restart
                          </button>
                          {rowBusy && <span className="text-xs text-gray-400">working…</span>}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AIDeviceDescription({ deviceId }: { deviceId: number }) {
  // AI-generated "what does this machine do?" summary. Auto-loads on select;
  // the backend generates + caches on first request and returns the cache
  // thereafter. Renders nothing when the AI master flag is off.
  const [state, setState] = React.useState<{
    enabled: boolean;
    description: string | null;
    generated_at: string | null;
  } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(
    (refresh: boolean) => {
      setLoading(true);
      setError("");
      api
        .get(`/api/v1/ai/devices/${deviceId}/description`, { params: refresh ? { refresh: true } : {} })
        .then((r) => setState(r.data))
        .catch((e) => setError(e.response?.data?.detail || "Couldn't generate a description."))
        .finally(() => setLoading(false));
    },
    [deviceId],
  );

  React.useEffect(() => {
    load(false);
  }, [load]);

  // AI off → show nothing (the feature is opt-in via AI settings).
  if (state && !state.enabled) return null;
  if (error && !state) {
    // AI enabled but the call failed (e.g. provider not configured). Keep it
    // quiet but visible so an admin knows why it's blank.
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 mb-6 text-xs text-gray-500 dark:text-gray-400">
        🤖 AI summary unavailable: {error}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-900/20 dark:to-gray-800 rounded-xl border border-indigo-100 dark:border-indigo-800/50 shadow-sm p-5 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
          <span>🤖</span> What this machine does
          <span className="text-[10px] font-normal uppercase tracking-wide bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 px-1.5 py-0.5 rounded">
            AI
          </span>
        </h2>
        <button
          onClick={() => load(true)}
          disabled={loading}
          className="text-xs text-indigo-600 dark:text-indigo-300 hover:underline disabled:opacity-50"
          title="Regenerate the AI description from the latest inventory"
        >
          {loading ? "Thinking…" : "↻ Regenerate"}
        </button>
      </div>
      {loading && !state?.description ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          Analyzing this machine…
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">
            {state?.description || "No description available."}
          </p>
          {state?.generated_at && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
              Generated {formatRelative(state.generated_at)}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function RemoteControlButton({ deviceId }: { deviceId: number }) {
  // Reflects whichever remote-access tool the MSP configured in Integrations
  // (GoToAssist / ScreenConnect / Splashtop / TeamViewer / AnyDesk). Hidden
  // when none is set up. Replaces the old hardcoded GoToAssist button.
  const [info, setInfo] = React.useState<{
    configured: boolean;
    vendor?: string;
    name?: string;
    icon?: string;
    installed?: boolean;
    console_url?: string | null;
    per_device_agent?: boolean;
  } | null>(null);

  React.useEffect(() => {
    let alive = true;
    api
      .get(`/api/v1/devices/${deviceId}/remote-access`)
      .then((r) => alive && setInfo(r.data))
      .catch(() => alive && setInfo(null));
    return () => {
      alive = false;
    };
  }, [deviceId]);

  // Hidden unless a remote-access integration is configured.
  if (!info || !info.configured) return null;

  const pending = !!info.per_device_agent && !info.installed;
  const onClick = () => {
    if (pending) {
      alert(
        `The ${info.name} agent isn't installed on this device yet. It installs ` +
          "automatically on the next check-in once the unattended installer is uploaded. " +
          `Opening the ${info.name} console anyway.`,
      );
    }
    if (info.console_url) window.open(info.console_url, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      onClick={onClick}
      className="text-xs bg-sky-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-sky-700"
      title={
        pending
          ? `Open your ${info.name} console (agent install pending)`
          : `Connect via your ${info.name} console`
      }
    >
      {info.icon || "🖥"} {info.name}
      {pending ? " (pending)" : ""}
    </button>
  );
}
